import { Component } from '@angular/core';
import {
  StatorParams,
  CoilTurn,
  CoilArc,
  TerminalPad,
  GerberData,
  BusRouting,
  BusArc,
  BusSegment,
  WindingType,
  CoilShape,
  CopperLayer,
  ChainingVia,
  ExitVia,
  BridgeVia,
  computeTurnLimits,
  computeCoilGerberData,
  computeBusRouting,
  computeReturnPads,
  layerNameByIndex,
  arcLargeArcFlag,
  buildCorners,
  buildForwardPath,
  pathMoment,
  VIA_PAD_MM,
} from './pcb-stator-geometry';
import { planCoilPolarities, assessRotatingField, recommendPoles, PoleOption } from './pcb-stator-polarity';
import {
  computeMagnetRecommendation,
  MagnetRecommendation,
  MagnetOption,
  MagnetShape,
} from './pcb-stator-magnets';
import { computeRotorLayout, RotorParams, RotorLayout, RotorRecessSpec, RECESS_GLUE_MM, RECESS_FIT_MM } from './pcb-stator-rotor';
import { generateRotorStl } from './pcb-stator-rotor-stl';
import { computeEncoderBands, EncoderBands } from './pcb-stator-encoder-bands';
import { buildSensingElement, SensingElement } from './pcb-stator-encoder-coils';
import {
  buildTargetRing, TargetRing, keyAzimuthRad, KEY_TAB_WIDTH_MM, KEY_TAB_DEPTH_MM, DISC_RIM_MM,
} from './pcb-stator-encoder-target';
import { CHIP } from './pcb-stator-encoder-chip';
import {
  buildEncoderBoard, buildTargetBoardDescriptor, EncoderBoardParams, EncoderBoardResult,
  VERIFIED_ENCODER_FIXTURE,
} from './pcb-stator-encoder-board';
import { computeCurrentRating, CurrentRating } from './pcb-stator-thermal';
import { phaseConductorSumMm2, computeVoltageGeneration, VoltageEstimate } from './pcb-stator-voltage';

/** One labeled what-if voltage figure for independent (6-lead) mode — a value
 *  that only exists if the leads are wired together in the described way. */
export interface VoltageScenario {
  key: 'wyeJumperedLineRmsV' | 'seriesTwoBoardPhaseRmsV';
  label: string;
  value: number;
}

/** Display-honest wrapper around `VoltageEstimate`: `primary` is the headline
 *  figure the stats footer shows, and `scenarios` is empty for wye/delta
 *  (nothing derived — the real line voltage is already primary) and carries
 *  the two labeled independent-mode what-ifs otherwise. */
export interface VoltageDisplay extends VoltageEstimate {
  primaryLabel: string;
  primary: number;
  scenarios: VoltageScenario[];
}
import { TerminalStyle, TERMINAL_STYLES } from './pcb-stator-terminals';
import { computeSilkLabels, SilkLabel, SILK_STROKE_MM, computeEarSilkMarks, EarSilkMark } from './pcb-stator-silkscreen';
import {
  MountingScrew,
  MOUNTING_SCREWS,
  MountingHole,
  computeMountingHoles,
  mountingHolesFit,
} from './pcb-stator-mounting';
import { STATOR_HELP, HELP_BY_KEY, HelpSection } from './pcb-stator-help';
import { assessConfigSupport, assessEncoderSupport, SupportAssessment } from './pcb-stator-support';
import {
  SavedStatorConfig, StatorInputs, HeadlineStats,
  loadAll, saveAll, addConfig, deleteConfig, renameConfig, makeId,
} from './pcb-stator-storage';
import {
  copperFileFunction, renderCopperLayer, renderSolderMask, renderDrillFile, renderMountingDrill, renderEdgeCuts,
  renderGenericOutline, emitBoard, BoardDescriptor, BoardLayerSpec, GerberDraw, DrillSpec,
} from './pcb-stator-gerber';
import {
  computeStackingEars, assessEarLayout, buildOutlineWithEars, stackRotationRad, rotateStackingEars, rotatePoints,
  StackingEar, EarLayoutAssessment,
} from './pcb-stator-stacking-tabs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

interface LayerInfo {
  index: number;
  label: string;
  color: string;
}

export type SectionKey = 'board' | 'coil' | 'stack' | 'generator' | 'magnets' | 'rotor' | 'sensor' | 'boards' | 'downloads';

/** Encoder-tab params (Task 9). Extends the board-builder's own param shape
 *  with the two extra dims the target ring / rotor recess need — kept as one
 *  object so the whole tuple is a single JSON.stringify cache key. */
export interface EncoderParams extends EncoderBoardParams {
  targetThicknessMm: number;
  hubRadiusMm: number;
  sensingGapMm: number;   // physical stack air gap (encoder ring <-> target); documentation-only, feeds the assembly README
}

@Component({
    selector: 'app-pcb-stator',
    templateUrl: './pcb-stator.component.html',
    styleUrls: ['./pcb-stator.component.scss'],
    standalone: false
})
export class PcbStatorComponent {

  readonly Math = Math;

  readonly helpSections: HelpSection[] = STATOR_HELP;

  constructor(private modal: NgbModal) {}

  /** Tooltip text for a control key; '' for unknown keys (ngbTooltip shows nothing). */
  help(key: string): string {
    return HELP_BY_KEY[key] ?? '';
  }

  /** Open the legend modal from an inline <ng-template>. */
  openLegend(content: unknown): void {
    this.modal.open(content, { size: 'lg', scrollable: true });
  }

  suggestedName(): string {
    return `${this.params.outerRadius * 2}mm · ${this.params.numPhases}φ · ${this.params.numPoles}P`;
  }

  private gatherInputs(): StatorInputs {
    return {
      params: { ...this.params },
      windingType: this.windingType,
      coilShape: this.coilShape,
      terminalStyle: this.terminalStyle,
      terminalLayout: this.terminalLayout,
      mountingHoles: this.mountingHoles,
      mountingScrew: this.mountingScrew,
      mountingHoleDiaMm: this.mountingHoleDiaMm,
      mountingBoltRadius: this.mountingBoltRadius,
      deltaTempC: this.deltaTempC,
      airGapT: this.airGapT,
      genRpm: this.genRpm,
      rotorMagnetShape: this.rotorMagnetShape,
      rotorMagnetThicknessMm: this.rotorMagnetThicknessMm,
      rotorDiscThicknessMm: this.rotorDiscThicknessMm,
      rotorBoreDiameterMm: this.rotorBoreDiameterMm,
      rotorPocketClearanceMm: this.rotorPocketClearanceMm,
    };
  }

  /** wye/delta: real line-to-line RMS (unchanged, byte-stable payload). Independent:
   *  `voltageData.lineRmsV` degenerates to the phase figure in the pure module (no
   *  wye/delta connection to derive a line voltage from) but reads exactly like a
   *  real one — `voltageDisplay.primary` is the same honest per-phase EMF the stats
   *  footer shows (Task 4), so the saved snapshot can never claim a line voltage the
   *  config does not have (save-honesty audit, Task 5). */
  private snapshotStats(): HeadlineStats {
    return {
      boardDiameterMm: this.params.outerRadius * 2,
      resistancePerPhaseOhm: this.estimatedResistancePerPhaseOhm,
      lineVoltageRms: this.voltageDisplay.primary,
    };
  }

  buildCurrentConfig(name: string): SavedStatorConfig {
    return {
      id: makeId(),
      name,
      createdAt: Date.now(),
      schemaVersion: 1,
      inputs: this.gatherInputs(),
      headlineStats: this.snapshotStats(),
    };
  }

  applyConfig(saved: SavedStatorConfig): void {
    const i = saved.inputs;
    this.params = { ...i.params };            // fresh object → invalidates viz/bus cache keys
    this.windingType = i.windingType;
    this.coilShape = i.coilShape;
    this.terminalStyle = i.terminalStyle;
    // Older saved configs (before Task 5) never wrote this field — default 'adjacent'.
    this.terminalLayout = i.terminalLayout ?? 'adjacent';
    this.mountingHoles = i.mountingHoles;
    this.mountingScrew = i.mountingScrew;
    this.mountingHoleDiaMm = i.mountingHoleDiaMm;
    this.mountingBoltRadius = i.mountingBoltRadius;
    this.deltaTempC = i.deltaTempC;
    this.airGapT = i.airGapT;
    this.genRpm = i.genRpm;
    this.rotorMagnetShape = i.rotorMagnetShape;
    this.rotorMagnetThicknessMm = i.rotorMagnetThicknessMm;
    this.rotorDiscThicknessMm = i.rotorDiscThicknessMm;
    this.rotorBoreDiameterMm = i.rotorBoreDiameterMm;
    this.rotorPocketClearanceMm = i.rotorPocketClearanceMm;
    // view state: reset layer visibility to match the loaded layer count (all on)
    this.visibleLayers = Array.from({ length: this.params.numLayers }, () => true);
  }

  saveCurrent(name: string): void {
    const finalName = name.trim() || this.suggestedName();
    const next = addConfig(this.savedConfigs, this.buildCurrentConfig(finalName));
    saveAll(next);
    this.savedConfigs = next;
  }

  loadConfig(saved: SavedStatorConfig): void {
    this.applyConfig(saved);
  }

  removeConfig(id: string): void {
    const next = deleteConfig(this.savedConfigs, id);
    saveAll(next);
    this.savedConfigs = next;
  }

  renameSaved(id: string, name: string): void {
    const trimmed = name.trim();
    if (!trimmed) return;   // blank rename → keep the entry's existing name unchanged
    const next = renameConfig(this.savedConfigs, id, trimmed);
    saveAll(next);
    this.savedConfigs = next;
  }

  openSavedModal(tpl: unknown): void {
    this.savedConfigs = loadAll();   // pick up any external changes
    this.modal.open(tpl, { size: 'lg', scrollable: true });
  }

  promptRename(current: string): string {
    return window.prompt('Rename configuration', current) ?? current;
  }

  confirmRemove(cfg: SavedStatorConfig): void {
    if (window.confirm(`Delete “${cfg.name}”?`)) this.removeConfig(cfg.id);
  }

  // Via / pad dimensions (mm)
  readonly VIA_DRILL_MM  = 0.3;
  readonly VIA_PAD_MM    = VIA_PAD_MM;   // canonical value lives in geometry (Task 4b)
  readonly MASK_EXPANSION = 0.05;

  params: StatorParams = {
    innerRadius:    20,
    outerRadius:    60,
    numPhases:       3,
    numPoles:        8,
    numCoils:        6,
    traceWidth:     0.5,
    traceClearance: 0.5,
    numLayers:       4,
    copperOz:        1,
    outerMargin:     5,
    innerMargin:     5,
  };

  windingType: WindingType = 'wye';
  coilShape: CoilShape = 'spiral';
  terminalStyle: TerminalStyle = 'solder';
  /** Terminal layout for the 6-lead 'independent' winding. 'adjacent' (default —
   *  byte-golden for every winding) drills return pads beside the drive pads;
   *  'stacking-tabs' routes the phase leads out to edge ears so identical boards
   *  stack hole-to-hole. Meaningful only with `windingType === 'independent'`; the
   *  emission/routing honours it only when `assessEarLayout` is valid (else it
   *  falls back to 'adjacent' with the honest plain-circle outline). */
  terminalLayout: 'adjacent' | 'stacking-tabs' = 'adjacent';
  mountingHoles = 0;                          // count; 0 = feature off
  mountingScrew: MountingScrew = 'M3';
  mountingHoleDiaMm: number | null = null;    // manual override; null = use preset
  mountingBoltRadius = 57.5;                  // outerRadius(60) - outerMargin(5)/2
  deltaTempC = 30;
  airGapT = 0.4;
  genRpm = 3000;
  visibleLayers: boolean[] = [true, true, true, true];
  isGenerating = false;

  showVias = true;        // chaining + bridge via dots
  showTerminals = true;   // terminal pads, exit-via markers, neutral node
  showBadges = true;      // N/S polarity badges
  // (legacy showRotor overlay toggle removed 2026-07-13 — the Boards:Rotor
  //  toggle, previewBoardVisible('rotor'), is the single gate for the rotor.)
  rotorMagnetShape: MagnetShape = 'round';
  rotorMagnetThicknessMm = 3;
  rotorDiscThicknessMm = 3;
  rotorBoreDiameterMm = 8;
  rotorPocketClearanceMm = 0.2;

  savedConfigs: SavedStatorConfig[] = loadAll();

  // ── UI layout state (tabs / accordion / stats footer) — plain fields, no DI ──
  activeTab: 'stator' | 'rotor' | 'encoder' = 'stator';
  statsCollapsed = true;
  sectionCollapsed: Record<SectionKey, boolean> = {
    board: false, coil: false, stack: false, generator: false, magnets: false, rotor: false,
    sensor: false, boards: false, downloads: false,
  };
  toggleSection(key: SectionKey): void {
    this.sectionCollapsed[key] = !this.sectionCollapsed[key];
  }

  /** Switches tabs and (for 'encoder', when the feature is on) kicks off the
   *  async board solve so the first visit to the tab is the "first preview
   *  render" trigger the perf note calls for — never a getter. */
  setActiveTab(tab: 'stator' | 'rotor' | 'encoder'): void {
    this.activeTab = tab;
    if (tab === 'encoder' && this.encoderEnabled) this.ensureEncoderBoard();
  }

  // ── Encoder tab (Task 9) ────────────────────────────────────────────────────
  // Default OFF: with encoderEnabled=false, encoderRecessSpec is undefined and
  // rotorLayout is called exactly as before — byte-identical rotor output.
  encoderEnabled = false;
  showBoardStator = true;
  showBoardRotor = true;
  showBoardEncoder = true;
  /** Preview-only "ghost" of the NEXT stacked board (stacking-tabs campaign),
   *  rotated by one electrical period D so the mating alignment — the
   *  ghost's + ears landing on THIS board's − ears — is visible directly.
   *  Default off; only ever rendered when `ghostStageVisible` (this AND
   *  `stackingTabsActive`) is true. See `ghostEars`/`ghostOutline` below. */
  showGhostStage = false;

  encoderParams: EncoderParams = {
    boreRadiusMm: 5, traceWidthMm: 0.3, clearanceMm: 0.3, rxTurns: 1,
    targetThicknessMm: 0.8, hubRadiusMm: 5, sensingGapMm: 1.0,
  };

  setEncoderEnabled(v: boolean): void {
    this.encoderEnabled = v;
    if (v) this.ensureEncoderBoard();
  }

  /** Single source of truth for "is this board's preview <g> visible" — the
   *  template's *ngIf reads THIS (not the raw showBoard* flags directly) so a
   *  test can assert on exactly the condition that gates the SVG group. The
   *  encoder group additionally requires the feature to be enabled (there is
   *  nothing to show otherwise). */
  previewBoardVisible(board: 'stator' | 'rotor' | 'encoder'): boolean {
    if (board === 'stator') return this.showBoardStator;
    if (board === 'rotor') return this.showBoardRotor;
    return this.showBoardEncoder && this.encoderEnabled;
  }

  readonly phaseLabels = ['U', 'V', 'W', 'X', 'Y', 'Z'];
  readonly phaseColors = ['#e76f51', '#4ade80', '#818cf8', '#f59e0b', '#ec4899', '#06b6d4'];
  private readonly layerPalette = ['#f87171', '#fbbf24', '#60a5fa', '#34d399', '#c084fc', '#fb923c', '#a78bfa', '#38bdf8'];

  // ── Derived geometry ──────────────────────────────────────────────────────

  get sectorAngleRad(): number { return 2 * Math.PI / this.params.numCoils; }
  get sectorAngleDeg(): number { return 360 / this.params.numCoils; }

  get tracePitch(): number { return this.params.traceWidth + this.params.traceClearance; }
  get copperThicknessMm(): number { return this.params.copperOz * 0.035; }

  get coilOuter(): number { return this.params.outerRadius - this.params.outerMargin; }
  get coilInner(): number { return this.params.innerRadius + this.params.innerMargin; }

  get termPadMm(): number { return TERMINAL_STYLES[this.terminalStyle].padMm; }
  get termHoleMm(): number { return TERMINAL_STYLES[this.terminalStyle].holeMm; }

  private _earLayoutCache: EarLayoutAssessment | null = null;
  private _earLayoutCacheKey = '';

  /** Memoized `assessEarLayout` for the CURRENT params/terminal dims — the single
   *  source `stackingTabsActive` (validity) AND the preview (`previewEars`,
   *  `previewOutline`, `earSilkMarks`) AND `configSupport` (collision reasons)
   *  all read, so none of them can independently recompute a different answer
   *  for the same params (the coilFlips/T1 coupling-audit pattern applied here). */
  get earLayoutAssessment(): EarLayoutAssessment {
    const key = JSON.stringify(this.params) + '|' + this.termPadMm + '|' + this.termHoleMm;
    if (key !== this._earLayoutCacheKey) {
      this._earLayoutCache = assessEarLayout(this.params, { padMm: this.termPadMm, holeMm: this.termHoleMm });
      this._earLayoutCacheKey = key;
    }
    return this._earLayoutCache!;
  }

  /** True when stacking-tabs ears should actually be emitted: the layout is
   *  selected, the winding is 'independent', AND the ear layout is collision-free
   *  (`assessEarLayout`). When false, everything falls back to the 'adjacent'
   *  return-pad path and the plain-circle outline — no ear pads/tails/T3 drills. */
  get stackingTabsActive(): boolean {
    return this.terminalLayout === 'stacking-tabs'
        && this.windingType === 'independent'
        && this.earLayoutAssessment.valid;
  }

  /** Preview-only: the six ears to draw (outline + hole dots + silk ticks), or
   *  [] whenever stacking-tabs isn't actually active — so the SVG/silk blocks
   *  never need to re-derive the same gating condition themselves. */
  get previewEars(): StackingEar[] {
    return this.stackingTabsActive ? this.earLayoutAssessment.ears : [];
  }

  /** Preview-only: the board's outer profile with ear protrusions spliced in
   *  (one closed simple loop) — drawn instead of the plain outer circle when
   *  stacking-tabs is active, mirroring what `generateEdgeCuts` fabs. Falls
   *  back to the same sampled plain circle `generateEdgeCuts` uses otherwise
   *  (NOT `buildOutlineWithEars` with an empty ear list — that function
   *  assumes at least one ear when splicing and is only ever called, here or
   *  in `generateEdgeCuts`, behind a `stackingTabsActive` gate). */
  get previewOutline(): Array<[number, number]> {
    return this.stackingTabsActive
      ? buildOutlineWithEars(this.params, this.earLayoutAssessment.ears)
      : this.sampleBoreLoop(this.params.outerRadius);
  }

  /** Stage rotation between successive boards in a stacking-tabs stack, in
   *  degrees — legend/README display value; live-derived from pole count
   *  (`stackRotationRad`), never hardcoded. */
  get stackStageDeg(): number {
    return (stackRotationRad(this.params) * 180) / Math.PI;
  }

  /** Preview-only: one stage-mating registration tick + compound label per ear
   *  (via the silkscreen module — see `computeEarSilkMarks`), or [] when
   *  stacking-tabs isn't active. */
  get earSilkMarks(): EarSilkMark[] {
    return this.stackingTabsActive
      ? computeEarSilkMarks(this.earLayoutAssessment.ears, this.phaseLabels)
      : [];
  }

  /** Single gating condition for the ghost "next stage" overlay <g> — mirrors
   *  `previewBoardVisible`: the toggle must be on AND stacking-tabs must
   *  actually be active (there is nothing valid to ghost otherwise). Every
   *  ghost-* getter below reads THIS, so none of them can drift apart. */
  get ghostStageVisible(): boolean {
    return this.showGhostStage && this.stackingTabsActive;
  }

  /** Preview-only: the NEXT stacked board's six ears — the CURRENT (memoized)
   *  `earLayoutAssessment.ears`, rigidly rotated by one electrical period D
   *  (`stackRotationRad`). Never recomputed from params: rotating the real
   *  ear layout is what makes the ghost's + ears land exactly on this board's
   *  − ears (the MATING ALIGNMENT invariant, `pcb-stator-stacking-tabs.ts`). */
  get ghostEars(): StackingEar[] {
    return this.ghostStageVisible
      ? rotateStackingEars(this.earLayoutAssessment.ears, stackRotationRad(this.params))
      : [];
  }

  /** Preview-only: the NEXT stacked board's eared outline — `previewOutline`
   *  (already the spliced eared loop when stacking-tabs is active), rotated
   *  the same way as `ghostEars`. Board outline ONLY: no coils, no routing —
   *  the ghost exists purely to show the mating seam. */
  get ghostOutline(): Array<[number, number]> {
    return this.ghostStageVisible
      ? rotatePoints(this.previewOutline, stackRotationRad(this.params))
      : [];
  }

  /** Preview-only: one compound label ('U+'/'U−'/…) per ghost ear, via the
   *  same silkscreen module the base board's own `earSilkMarks` use. This is
   *  the payoff: the ghost's own +-ear labels render right on top of the
   *  base board's −-ear holes, naming exactly which lead mates with which. */
  get ghostEarSilkMarks(): EarSilkMark[] {
    return this.ghostStageVisible ? computeEarSilkMarks(this.ghostEars, this.phaseLabels) : [];
  }

  get mountingHoleDia(): number {
    return this.mountingHoleDiaMm ?? MOUNTING_SCREWS[this.mountingScrew];
  }

  private _mountCache: MountingHole[] | null = null;
  private _mountCacheKey = '';

  get mountingHoleList(): MountingHole[] {
    const key = [
      this.mountingHoles, this.mountingHoleDia,
      this.mountingBoltRadius, this.params.traceClearance,
    ].join('|');
    if (key !== this._mountCacheKey) {
      this._mountCache = computeMountingHoles({
        count: this.mountingHoles,
        holeMm: this.mountingHoleDia,
        boltRadius: this.mountingBoltRadius,
        startAngleRad: -Math.PI / 2,
        traceClearance: this.params.traceClearance,
      });
      this._mountCacheKey = key;
    }
    return this._mountCache!;
  }

  get mountingHolesValid(): boolean {
    return this.mountingHoles === 0 ||
      mountingHolesFit(this.mountingHoleList, this.params.innerRadius, this.params.outerRadius);
  }

  /** Radial depth available for winding (mm). */
  get radialDepthMm(): number {
    return Math.max(0, this.coilOuter - this.coilInner - 2 * this.params.traceClearance);
  }

  /** Auto-computed turn limits from the geometry module. */
  get turnLimits() { return computeTurnLimits(this.params, this.coilShape); }

  /** Auto-computed turn count used by every coil (radial ∩ angular). */
  get turnsPerLayer(): number { return this.turnLimits.total; }

  /** Outermost arc radius of each coil (same for every turn 0). */
  get rCoilOuter0(): number {
    return this.coilOuter - this.params.traceClearance;
  }

  /** Innermost arc radius of each coil (turn N−1). */
  get rCoilInnerLast(): number {
    return this.coilInner + this.params.traceClearance;
  }

  /** Coils per phase. */
  get coilsPerPhase(): number {
    return Math.max(1, Math.floor(this.params.numCoils / this.params.numPhases));
  }

  /** Tiered fabricability assessment for the current configuration. */
  get configSupport(): SupportAssessment {
    return assessConfigSupport({
      numCoils: this.params.numCoils,
      numPhases: this.params.numPhases,
      numPoles: this.params.numPoles,
      windingType: this.windingType,
      terminalStyle: this.terminalStyle,
      coilShape: this.coilShape,
      deltaBusDropped: this.busData.deltaBusDropped,
      innerJoinsDropped: this.busData.innerJoinsDropped,
      outerJoinsDropped: this.busData.outerJoinsDropped,
      hookupsDropped: this.vizData.hookupsDropped,
      rotatingFieldOk: assessRotatingField({
        numCoils: this.params.numCoils,
        numPhases: this.params.numPhases,
        numPoles: this.params.numPoles,
      }).balanced,
      numLayers: this.params.numLayers,
      terminalLayout: this.terminalLayout,
      earLayoutValid: this.earLayoutAssessment.valid,
      earLayoutReasons: this.earLayoutAssessment.reasons,
    });
  }

  private _poleOptsCache: PoleOption[] | null = null;
  private _poleOptsCacheKey = '';

  /** Balanced pole recommendations for the current coil/phase count (top 6). */
  get poleOptions(): PoleOption[] {
    const key = `${this.params.numCoils}|${this.params.numPhases}`;
    if (key !== this._poleOptsCacheKey) {
      this._poleOptsCache = recommendPoles(this.params.numCoils, this.params.numPhases)
        .filter(o => o.balanced)
        .slice(0, 6);
      this._poleOptsCacheKey = key;
    }
    return this._poleOptsCache!;
  }

  setPoles(poles: number): void {
    this.params.numPoles = poles;
  }

  // ── Validation ────────────────────────────────────────────────────────────

  get numCoilsValid(): boolean {
    return this.params.numCoils >= this.params.numPhases &&
           this.params.numCoils % this.params.numPhases === 0;
  }

  get turnsValid(): boolean {
    return this.turnsPerLayer >= 2;
  }

  get marginsValid(): boolean {
    // Need room for numPhases staggered bus arcs + 2x clearance for safe-zone boundaries.
    const p = this.params.traceWidth + this.params.traceClearance;
    const minMargin = 2 * this.params.traceClearance + (this.params.numPhases + 1) * p;
    return this.params.outerMargin >= minMargin && this.params.innerMargin >= minMargin;
  }

  // ── Layer info ────────────────────────────────────────────────────────────

  get layers(): LayerInfo[] {
    return Array.from({ length: this.params.numLayers }, (_, i) => ({
      index: i,
      label: this.getLayerName(i).replace('_', '.'),
      color: this.layerPalette[i % this.layerPalette.length],
    }));
  }

  isLayerVisible(i: number): boolean {
    return i < this.visibleLayers.length ? this.visibleLayers[i] : true;
  }

  /** Visibility for copper referenced by layer NAME (bus arcs/segments carry
   *  CopperLayer names, while the toggles are indexed numerically). */
  isCopperLayerVisible(name: CopperLayer): boolean {
    for (let i = 0; i < this.params.numLayers; i++) {
      if (layerNameByIndex(i, this.params.numLayers) === name) return this.isLayerVisible(i);
    }
    return true;
  }

  toggleLayer(i: number): void {
    while (this.visibleLayers.length <= i) this.visibleLayers.push(true);
    this.visibleLayers[i] = !this.visibleLayers[i];
  }

  setNumLayers(n: number): void {
    if (![4, 6, 8].includes(n)) return;
    this.params.numLayers = n;
    const prev = this.visibleLayers;
    this.visibleLayers = Array.from({ length: n }, (_, i) => prev[i] ?? true);
  }

  // ── SVG visualisation ─────────────────────────────────────────────────────

  private _vizCache: GerberData | null = null;
  private _vizCacheKey = '';

  get vizData(): GerberData {
    const key = JSON.stringify(this.params) + '|' + this.windingType + '|' + this.coilShape + '|' + this.terminalStyle + '|' + this.terminalLayout;
    if (key !== this._vizCacheKey) {
      this._vizCache    = this.computeGerberData();
      this._vizCacheKey = key;
    }
    return this._vizCache!;
  }

  private _flipsCache: boolean[] | null = null;
  private _flipsCacheKey = '';

  /** Per-coil mirror flags for the CURRENT params — the single source threaded into
   *  `vizData`/`computeGerberData` (drive terminals + independent-mode return pads)
   *  AND `busData` (bus/tail routing), so all three can never derive DIFFERENT flip
   *  states for the same coil (T1 coupling audit, Task 5: `computeCoilGerberData`,
   *  `computeBusRouting`, and `computeReturnPads` each independently default to
   *  `planCoilPolarities(params).flip` when given no explicit override — they have
   *  always agreed in practice because nothing here ever passed a non-default
   *  override, but that agreement was coincidental, not structural. This getter
   *  makes it structural: every caller below reads the same memoized array). */
  get coilFlips(): boolean[] {
    const key = JSON.stringify(this.params);
    if (key !== this._flipsCacheKey) {
      this._flipsCache    = planCoilPolarities(this.params).flip;
      this._flipsCacheKey = key;
    }
    return this._flipsCache!;
  }

  private _busCache: BusRouting | null = null;
  private _busCacheKey = '';

  get busData(): BusRouting {
    const key = JSON.stringify(this.params) + '|' + this.windingType + '|' + this.coilShape + '|' + this.terminalStyle + '|' + this.terminalLayout;
    if (key !== this._busCacheKey) {
      this._busCache    = computeBusRouting(
        this.params, this.phaseLabels, this.windingType, this.coilShape, this.coilFlips,
        { padMm: this.termPadMm, holeMm: this.termHoleMm },
        { terminalLayout: this.terminalLayout },
      );
      this._busCacheKey = key;
    }
    return this._busCache!;
  }

  private _magnetCache: MagnetRecommendation | null = null;
  private _magnetCacheKey = '';

  get magnetData(): MagnetRecommendation {
    const { innerRadius, outerRadius, innerMargin, outerMargin, numPoles } = this.params;
    const key = JSON.stringify({ innerRadius, outerRadius, innerMargin, outerMargin, numPoles });
    if (key !== this._magnetCacheKey) {
      this._magnetCache = computeMagnetRecommendation({
        innerRadius, outerRadius, innerMargin, outerMargin, numPoles,
      });
      this._magnetCacheKey = key;
    }
    return this._magnetCache!;
  }

  /** The magnet option matching the shape the rotor is actually built for
   *  (rotorMagnetShape) — the recommendation panel shows both round and rect,
   *  but only one drives the rotor/catalog-cap hint. */
  get activeMagnetOption(): MagnetOption | undefined {
    return this.magnetData.options.find(o => o.shape === this.rotorMagnetShape);
  }

  /** Catalog-cap hint for the active magnet shape, or null when not capped
   *  (or nothing fits at all). See CATALOG_CAP_HINT_RATIO in
   *  pcb-stator-magnets.ts for the 25%-loss rationale (2026-07-26). */
  get magnetCatalogCapHint(): string | null {
    const opt = this.activeMagnetOption;
    if (!opt || !opt.catalogCapped || !opt.standardMm) return null;
    const fits = 'dia' in opt.fitsMm ? opt.fitsMm.dia : opt.fitsMm.length;
    const std = 'dia' in opt.standardMm ? opt.standardMm.dia : opt.standardMm.length;
    return `catalog-capped: geometry fits ${fits.toFixed(1)}mm but largest standard size is ${std}mm — a custom-size override or multiple magnets per pole would fill the sector (backlog)`;
  }

  get rotorParams(): RotorParams {
    return {
      magnetShape: this.rotorMagnetShape,
      magnetThicknessMm: this.rotorMagnetThicknessMm,
      discThicknessMm: this.rotorDiscThicknessMm,
      boreDiameterMm: this.rotorBoreDiameterMm,
      pocketClearanceMm: this.rotorPocketClearanceMm,
    };
  }

  get rotorLayout(): RotorLayout {
    return computeRotorLayout(this.magnetData, this.rotorParams, this.encoderRecessSpec);
  }

  get rotorStlBytes(): ArrayBuffer {
    return generateRotorStl(this.rotorLayout);
  }

  // ── Encoder derived geometry (Task 9) ───────────────────────────────────────
  // Cheap modules (bands / target ring / sensing element) are plain memoized
  // getters — ms-fast. buildEncoderBoard is NOT: it runs a ~7s constraint
  // solver and must never be called from a getter / the change-detection
  // path — see encoderBoard / ensureEncoderBoard below.

  /** Pole-0 datum shared by the rotor notch, coil, and target-ring generators.
   *  Not exported by pcb-stator-encoder-board.ts (a pure, no-DI module), so it
   *  is duplicated here — same convention documented there — to keep the
   *  preview/recess wiring bit-identical to what buildEncoderBoard uses. */
  readonly ENCODER_DATUM_RAD = -Math.PI / 2;

  /** Fixed by the RAA2P3226 (CHIP.finePeriodsAllowed[0] / coarsePeriods) —
   *  identical to what buildEncoderBoard uses internally, so the preview
   *  always matches what a download would emit. */
  get encoderPeriods(): { fine: number; coarse: number } {
    return { fine: CHIP.finePeriodsAllowed[0], coarse: CHIP.coarsePeriods };
  }

  /** Display-only — NOT a user input (the brief: "fine periodicity displays
   *  as numPoles/2"). Electrical periods/rev the encoder is meant to track,
   *  shown next to the (fixed) actual chip periodicity above for comparison. */
  get finePeriodicity(): number {
    return this.params.numPoles / 2;
  }

  private _encoderBandsCache: EncoderBands | null = null;
  private _encoderBandsCacheKey = '';

  /** Sensor track annuli, derived from the rotor's POCKET geometry alone (no
   *  recess — computing the recess needs the bands, so basing bands on a
   *  recess would be circular; recessSpec below closes the loop with a
   *  second, still-cheap, computeRotorLayout call). */
  get encoderBands(): EncoderBands {
    const key = JSON.stringify(this.magnetData) + '|' + JSON.stringify(this.rotorParams) + '|' + this.encoderParams.hubRadiusMm;
    if (key !== this._encoderBandsCacheKey) {
      const pocketLayout = computeRotorLayout(this.magnetData, this.rotorParams);
      this._encoderBandsCache = computeEncoderBands(pocketLayout, this.encoderParams.hubRadiusMm);
      this._encoderBandsCacheKey = key;
    }
    return this._encoderBandsCache!;
  }

  private _targetRingCache: TargetRing | null = null;
  private _targetRingCacheKey = '';

  get targetRing(): TargetRing {
    const key = JSON.stringify(this.encoderBands) + '|' + this.encoderParams.boreRadiusMm + '|' + this.encoderParams.targetThicknessMm;
    if (key !== this._targetRingCacheKey) {
      this._targetRingCache = buildTargetRing(
        this.encoderBands, this.encoderPeriods, this.ENCODER_DATUM_RAD,
        this.encoderParams.boreRadiusMm, this.encoderParams.targetThicknessMm,
      );
      this._targetRingCacheKey = key;
    }
    return this._targetRingCache!;
  }

  private _sensingElementCache: SensingElement | null = null;
  private _sensingElementCacheKey = '';

  get sensingElement(): SensingElement {
    const key = JSON.stringify(this.encoderBands) + '|' +
      this.encoderParams.traceWidthMm + '|' + this.encoderParams.clearanceMm + '|' + this.encoderParams.rxTurns;
    if (key !== this._sensingElementCacheKey) {
      this._sensingElementCache = buildSensingElement(this.encoderBands, this.encoderPeriods, {
        traceWidthMm: this.encoderParams.traceWidthMm, clearanceMm: this.encoderParams.clearanceMm,
        rxTurns: this.encoderParams.rxTurns, datumRad: this.ENCODER_DATUM_RAD,
      });
      this._sensingElementCacheKey = key;
    }
    return this._sensingElementCache!;
  }

  /** Back-face recess request for the rotor STL — the single-source helper
   *  (keyAzimuthRad, from pcb-stator-encoder-target) derives the key angle so
   *  the target tab and the rotor's mating finger can never drift apart.
   *  undefined (no recess -> byte-identical rotor) unless the feature is on
   *  AND the bands actually fit. */
  get encoderRecessSpec(): RotorRecessSpec | undefined {
    if (!this.encoderEnabled || !this.encoderBands.valid) return undefined;
    return {
      // Inner wall follows the target's SUBSTRATE bore, not its copper extent
      // (2026-07-13 inner-land fix): the disc substrate spans [boreRadiusMm,
      // disc OD], so a recess starting at coarse.rInMm would leave the inner
      // annulus resting on un-recessed rotor face — the whole disc proud again.
      innerRadiusMm: this.encoderParams.boreRadiusMm,
      outerRadiusMm: this.encoderBands.fine.rOutMm + DISC_RIM_MM,
      targetThicknessMm: this.encoderParams.targetThicknessMm,
      keyAngleRad: keyAzimuthRad(this.ENCODER_DATUM_RAD, this.encoderPeriods.fine),
      keyWidthMm: KEY_TAB_WIDTH_MM,
      keyTabDepthMm: KEY_TAB_DEPTH_MM,
    };
  }

  private _encoderBoardCache: EncoderBoardResult | null = null;
  private _encoderBoardCacheKey = '';
  private _encoderBoardPromise: Promise<EncoderBoardResult> | null = null;
  private _encoderBoardPendingKey = '';
  /** True while the ~7s escape-routing solver is running (set just before the
   *  deferred solve, cleared when it resolves) — template busy state. */
  encoderBoardBusy = false;

  private get encoderBoardCacheKey(): string {
    return JSON.stringify(this.encoderParams) + '|' + JSON.stringify(this.encoderBands) + '|' + JSON.stringify(this.mountingHoleList);
  }

  /** Cached solved encoder-ring board for the CURRENT params, or null if it
   *  has not been (re)solved yet. Read-only — never runs the solver itself
   *  (that is the hard perf rule: this is a getter, so it sits in the
   *  change-detection path). Call ensureEncoderBoard() from a user gesture. */
  get encoderBoard(): EncoderBoardResult | null {
    return this._encoderBoardCacheKey === this.encoderBoardCacheKey ? this._encoderBoardCache : null;
  }

  /** Kicks off (or reuses) the async escape-routing solve. Idempotent against
   *  the current param key, so it is safe to call from every trigger (tab
   *  switch, enabling the feature, a download click) without re-solving.
   *  Deferred one macrotask (setTimeout 0) so the "solving routing…" busy
   *  flag has a chance to paint before the still-synchronous ~7s solver
   *  blocks the main thread — there is no worker / chunked-solve
   *  infrastructure in this module, so this is the closest a plain
   *  Zone.js Angular component can get to a non-frozen busy indicator. */
  ensureEncoderBoard(): Promise<EncoderBoardResult> {
    const key = this.encoderBoardCacheKey;
    if (this._encoderBoardCache && this._encoderBoardCacheKey === key) {
      return Promise.resolve(this._encoderBoardCache);
    }
    if (this._encoderBoardPromise && this._encoderBoardPendingKey === key) {
      return this._encoderBoardPromise;
    }
    this.encoderBoardBusy = true;
    this._encoderBoardPendingKey = key;
    const bands = this.encoderBands;
    const mounting = this.mountingHoleList;
    const params: EncoderBoardParams = {
      boreRadiusMm: this.encoderParams.boreRadiusMm,
      traceWidthMm: this.encoderParams.traceWidthMm,
      clearanceMm: this.encoderParams.clearanceMm,
      rxTurns: this.encoderParams.rxTurns,
    };
    const promise = new Promise<EncoderBoardResult>(resolve => {
      setTimeout(() => {
        const result = buildEncoderBoard(bands, CHIP, mounting, params);
        this._encoderBoardCache = result;
        this._encoderBoardCacheKey = key;
        this.encoderBoardBusy = false;
        if (this._encoderBoardPromise === promise) this._encoderBoardPromise = null;
        resolve(result);
      }, 0);
    });
    this._encoderBoardPromise = promise;
    return promise;
  }

  /** Full-input fingerprint against VERIFIED_ENCODER_FIXTURE (the exact tuple
   *  tools/pcb-stator-drc/generate.ts builds the raster-checked boards from —
   *  shared constant, so fixture and badge can never drift). The encoder
   *  boards' copper depends on far more than EncoderBoardParams: the sensing
   *  bands derive from the rotor pocket layout (stator radii/margins/poles +
   *  rotor params) and the hub radius, and the NPTH ring from the mounting
   *  config — so ALL of those are fingerprinted. Returns the names of every
   *  diverging input ([] = verified); the banner lists them. */
  get encoderVerifiedDivergences(): string[] {
    const f = VERIFIED_ENCODER_FIXTURE;
    const out: string[] = [];
    const cmp = (name: string, actual: unknown, expected: unknown) => {
      if (actual !== expected) out.push(name);
    };
    // Encoder board params + hub radius (bands input)
    cmp('boreRadiusMm', this.encoderParams.boreRadiusMm, f.encoderParams.boreRadiusMm);
    cmp('traceWidthMm', this.encoderParams.traceWidthMm, f.encoderParams.traceWidthMm);
    cmp('clearanceMm', this.encoderParams.clearanceMm, f.encoderParams.clearanceMm);
    cmp('rxTurns', this.encoderParams.rxTurns, f.encoderParams.rxTurns);
    cmp('hubRadiusMm', this.encoderParams.hubRadiusMm, f.hubRadiusMm);
    // Stator inputs the fixture's rotor/bands derive from
    cmp('innerRadius', this.params.innerRadius, f.stator.innerRadius);
    cmp('outerRadius', this.params.outerRadius, f.stator.outerRadius);
    cmp('innerMargin', this.params.innerMargin, f.stator.innerMargin);
    cmp('outerMargin', this.params.outerMargin, f.stator.outerMargin);
    cmp('numPoles', this.params.numPoles, f.stator.numPoles);
    // Rotor params (pocket geometry -> sensing bands)
    cmp('rotorMagnetShape', this.rotorMagnetShape, f.rotor.magnetShape);
    cmp('rotorMagnetThicknessMm', this.rotorMagnetThicknessMm, f.rotor.magnetThicknessMm);
    cmp('rotorDiscThicknessMm', this.rotorDiscThicknessMm, f.rotor.discThicknessMm);
    cmp('rotorBoreDiameterMm', this.rotorBoreDiameterMm, f.rotor.boreDiameterMm);
    cmp('rotorPocketClearanceMm', this.rotorPocketClearanceMm, f.rotor.pocketClearanceMm);
    // Mounting config (NPTH ring + copper keepouts)
    cmp('mountingHoles', this.mountingHoles, f.mounting.count);
    cmp('mountingHoleDia', this.mountingHoleDia, f.mounting.holeMm);
    cmp('mountingBoltRadius', this.mountingBoltRadius, f.mounting.boltRadius);
    cmp('traceClearance', this.params.traceClearance, f.mounting.traceClearance);
    return out;
  }

  get encoderParamsVerified(): boolean {
    return this.encoderVerifiedDivergences.length === 0;
  }

  /** Tiered fabricability assessment for the encoder tab — mirrors
   *  configSupport's philosophy (worst tier shown, downloads never block).
   *  Reads encoderBoard (a safe getter, never triggers the solver). */
  get encoderSupport(): SupportAssessment {
    const board = this.encoderBoard;
    return assessEncoderSupport({
      bandsValid: this.encoderBands.valid,
      bandsReasons: this.encoderBands.reasons,
      mountingHoles: this.mountingHoles,
      boardComputed: board !== null,
      boardValid: board?.valid ?? true,
      boardReasons: board?.reasons ?? [],
      keyFingerDropped: !!this.rotorLayout.keyFingerDropped,
      recessDropped: !!this.rotorLayout.recessDropped,
      divergingParams: this.encoderVerifiedDivergences,
    });
  }

  /** Preview tick marking the pole-0 index notch: radial segment straddling the rim. */
  get rotorNotchLine(): { x1: number; y1: number; x2: number; y2: number } | null {
    const l = this.rotorLayout;
    if (!l.valid) return null;
    const a = (l.notch.angleDeg * Math.PI) / 180;
    const r1 = l.discOuterRadiusMm - l.notch.depthMm, r2 = l.discOuterRadiusMm + l.notch.depthMm;
    return { x1: r1 * Math.cos(a), y1: r1 * Math.sin(a), x2: r2 * Math.cos(a), y2: r2 * Math.sin(a) };
  }

  get turnsByLayer(): CoilTurn[][] {
    const result: CoilTurn[][] = Array.from({ length: this.params.numLayers }, () => []);
    for (const t of this.vizData.turns) {
      if (t.layer < result.length) result[t.layer].push(t);
    }
    return result;
  }

  get coilArcsByLayer(): CoilArc[][] {
    const result: CoilArc[][] = Array.from({ length: this.params.numLayers }, () => []);
    for (const a of this.vizData.arcs) {
      if (a.layer < result.length) result[a.layer].push(a);
    }
    return result;
  }

  get vizChainingVias(): ChainingVia[] { return this.vizData.chainingVias; }
  get vizExitVias(): ExitVia[]         { return this.vizData.exitVias; }
  get vizTerminals(): TerminalPad[] { return this.vizData.terminals; }

  /** Silkscreen labels for the preview and F_Silk Gerber: one per drive terminal,
   *  plus one compound '<letter>−' label per return terminal (6-lead independent
   *  mode only — wye/delta terminals never carry role 'return', so their emitted
   *  F_Silk stays byte-identical). In independent mode the drive labels also gain
   *  the '+' suffix (suffixDrive) for the symmetric U+/U− board legend; the flag
   *  is mode-gated so wye/delta drive labels stay bare (byte-stability gate). */
  get silkLabels(): SilkLabel[] {
    return computeSilkLabels(
      // Ear joint pads are excluded — they carry their own stage-mating ticks (T5),
      // not the entry-pad glyph labels.
      this.vizTerminals.filter(t => (t.role === 'drive' || t.role === 'return') && !t.ear),
      this.phaseLabels,
      {
        termPadMm: this.termPadMm, outerRadius: this.params.outerRadius,
        suffixDrive: this.windingType === 'independent',
      },
    );
  }

  readonly SILK_STROKE_MM = SILK_STROKE_MM;

  /** Visual radius for terminal markers in the SVG preview. */
  get terminalMarkerR(): number {
    return Math.max(this.minStrokeWidth * 3, this.params.outerRadius * 0.025);
  }

  /** Per-coil polarity badge computed from the EMITTED geometry (not planner
   *  intent) so a chirality regression is visible in the preview.
   *  Convention: negative moment (math coords, same sign as an L0 forward
   *  spiral) = 'N' toward the viewer looking at F.Cu. */
  get coilPolarityBadges(): { x: number; y: number; label: 'N' | 'S' | '!'; phase: number; dead: boolean }[] {
    const data = this.vizData;
    const n = this.turnsPerLayer;
    if (n < 2) return [];
    const rBadge = (this.coilInner + this.coilOuter) / 2;
    const out: { x: number; y: number; label: 'N' | 'S' | '!'; phase: number; dead: boolean }[] = [];
    for (let c = 0; c < this.params.numCoils; c++) {
      const m = pathMoment({
        arcs: data.arcs.filter(a => a.coilIndex === c),
        legs: data.turns.filter(t => t.coilIndex === c),
      });
      const corners = buildCorners(this.coilShape, c, this.params, n);
      const ideal = 4 * Math.abs(pathMoment(buildForwardPath(c, 0, c % this.params.numPhases, corners)));
      const dead = Math.abs(m) < 0.25 * ideal;
      const thetaC = (2 * Math.PI * c) / this.params.numCoils - Math.PI / 2;
      out.push({
        x: rBadge * Math.cos(thetaC), y: rBadge * Math.sin(thetaC),
        label: dead ? '!' : (m < 0 ? 'N' : 'S'),
        phase: c % this.params.numPhases,
        dead,
      });
    }
    return out;
  }

  get windingFactors(): number[] {
    return planCoilPolarities(this.params).windingFactorPerPhase;
  }

  get windingFactorLow(): boolean {
    return this.windingFactors.some(wf => wf < 0.5);
  }

  /** Convert a CoilArc to an SVG arc path. */
  coilArcSvgPath(arc: CoilArc): string {
    return this.arcSvgPath(arc);
  }

  busArcSvgPath(arc: BusArc): string {
    return this.arcSvgPath(arc);
  }

  /** Shared origin-centred SVG arc path with a correctly-computed large-arc-flag. */
  private arcSvgPath(arc: CoilArc | BusArc): string {
    const sweep = arc.sweepCW ? 1 : 0;
    const large = arcLargeArcFlag(arc.startX, arc.startY, arc.endX, arc.endY, arc.sweepCW);
    return `M ${arc.startX.toFixed(4)} ${arc.startY.toFixed(4)} ` +
           `A ${arc.radius.toFixed(4)} ${arc.radius.toFixed(4)} 0 ${large} ${sweep} ` +
           `${arc.endX.toFixed(4)} ${arc.endY.toFixed(4)}`;
  }

  busTraceColor(a: BusArc | BusSegment): string {
    return this.phaseColors[a.phase];
  }

  // ── Encoder preview helpers (Task 9) ────────────────────────────────────────

  /** Straight-polyline SVG path 'd' — the encoder modules (CoilTrace, target
   *  lobes, board routes/pads) carry no arc/curvature data, unlike the
   *  stator's GerberDraw-free arc types above. */
  tracePathD(points: ReadonlyArray<readonly [number, number]>): string {
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0].toFixed(4)} ${p[1].toFixed(4)}`).join(' ');
  }

  /** Closed-polygon SVG path 'd' (target lobes, disc/recess outlines). */
  polygonPathD(points: ReadonlyArray<readonly [number, number]>): string {
    return this.tracePathD(points) + ' Z';
  }

  /** Net-family color for a coil/route trace — TX / fine-RX / coarse-RX /
   *  everything else (chip escape routes to header/caps), so the preview
   *  reads as "which track" without needing a full per-net legend. */
  encoderNetColor(net: string): string {
    if (net.startsWith('TX')) return '#f59e0b';
    if (net.startsWith('RX_FINE')) return '#4ade80';
    if (net.startsWith('RX_COARSE')) return '#818cf8';
    return '#cbd5e1';
  }

  // ── SVG helpers ───────────────────────────────────────────────────────────

  get svgViewBox(): string {
    const pad = Math.max(10, this.params.outerRadius * 0.28);
    const e = this.params.outerRadius + pad;
    return `${-e} ${-e} ${e * 2} ${e * 2}`;
  }

  get minStrokeWidth(): number {
    return this.params.traceWidth;
  }

  // ── Electrical estimates ──────────────────────────────────────────────────

  get estimatedResistancePerPhaseOhm(): number {
    const rho = 1.72e-5;
    const crossSection = this.params.traceWidth * this.copperThicknessMm;
    const n = this.turnsPerLayer;
    if (n < 2) return 0;

    // Conductor length from the ACTUAL coil corners, so the estimate is correct for
    // every shape (spiral turns taper; wedge turns keep full radial height).
    const corners = buildCorners(this.coilShape, 0, this.params, n);
    let totalLen = 0;
    for (let t = 0; t < n; t++) {
      const rO = corners.rOuter[t];
      const rI = corners.rInner[t];
      totalLen += 2 * (rO - rI);                                                 // two radial legs
      totalLen += rI * Math.max(corners.thetaR[t] - corners.thetaL[t], 0);       // inner arc
      if (t < n - 1) {
        totalLen += rO * Math.max(corners.thetaR[t] - corners.thetaL[t + 1], 0); // outer arc
        totalLen += rO - corners.rOuter[t + 1];                                  // radial step to next turn
      }
    }

    // Series chain: every layer's winding is traversed in series.
    const rPerCoil = rho * totalLen / crossSection * this.params.numLayers;
    return rPerCoil * this.coilsPerPhase;
  }

  get estimatedInductancePerPhaseH(): number {
    const n = this.turnsPerLayer;
    if (n < 2) return 0;
    const dOuter = 2 * this.rCoilOuter0 / 1000;
    const dInner = 2 * this.rCoilInnerLast / 1000;
    const dAvg = (dOuter + dInner) / 2;
    const fillRatio = Math.max((dOuter - dInner) / (dOuter + dInner), 0.01);
    const mu0 = 4 * Math.PI * 1e-7;

    const L_single = mu0 * n * n * dAvg *
      (Math.log(2.46 / fillRatio) + 0.2 * fillRatio * fillRatio);

    const L_coil = L_single;
    return L_coil * this.coilsPerPhase;
  }

  get currentRating(): CurrentRating {
    return computeCurrentRating(this.params.traceWidth, this.params.copperOz, this.deltaTempC);
  }

  get voltageData(): VoltageEstimate {
    const g = phaseConductorSumMm2(this.vizData, this.params.numPhases, 0);
    return computeVoltageGeneration(g, this.airGapT, this.genRpm, this.windingType, this.windingFactors[0]);
  }

  /**
   * Display-honest voltage summary. Wye/delta: unchanged from historical
   * behavior — `voltageData.lineRmsV` is a real terminal-to-terminal voltage,
   * so it stays the primary headline figure. Independent (6-lead) mode has no
   * internal star/delta connection, so `voltageData.lineRmsV` (computed as
   * phaseRmsV × 1 by the pure module, since it isn't 'wye') would read like a
   * meaningful line voltage but is not one — nothing ties the six leads
   * together without external wiring. Independent mode instead leads with the
   * per-phase open-circuit EMF and offers two labeled *if-you-wire-it-this-way*
   * derived scenarios; it never asserts a line-to-line number of its own.
   */
  get voltageDisplay(): VoltageDisplay {
    const v = this.voltageData;
    if (this.windingType === 'independent') {
      return {
        primaryLabel: 'V / phase (EMF)',
        primary: v.phaseRmsV,
        phaseRmsV: v.phaseRmsV,
        lineRmsV: v.lineRmsV,
        kePhaseVperKrpm: v.kePhaseVperKrpm,
        kw: v.kw,
        conductorSumMm2: v.conductorSumMm2,
        scenarios: [
          { key: 'wyeJumperedLineRmsV', label: 'Wye-jumpered (line)', value: v.phaseRmsV * Math.sqrt(3) },
          { key: 'seriesTwoBoardPhaseRmsV', label: '2-board series (phase)', value: v.phaseRmsV * 2 },
        ],
      };
    }
    return {
      primaryLabel: 'V line-line',
      primary: v.lineRmsV,
      phaseRmsV: v.phaseRmsV,
      lineRmsV: v.lineRmsV,
      kePhaseVperKrpm: v.kePhaseVperKrpm,
      kw: v.kw,
      conductorSumMm2: v.conductorSumMm2,
      scenarios: [],
    };
  }

  // ── Template helpers ──────────────────────────────────────────────────────

  phaseIndices(): number[] {
    return Array.from({ length: this.params.numPhases }, (_, i) => i);
  }

  formatOhms(ohms: number): string {
    if (ohms >= 1)     return `${ohms.toFixed(2)} Ω`;
    if (ohms >= 0.001) return `${(ohms * 1000).toFixed(1)} mΩ`;
    return `${(ohms * 1e6).toFixed(0)} µΩ`;
  }

  formatHenry(h: number): string {
    if (h >= 1e-3) return `${(h * 1000).toFixed(2)} mH`;
    if (h >= 1e-6) return `${(h * 1e6).toFixed(1)} µH`;
    return `${(h * 1e9).toFixed(0)} nH`;
  }

  magnetShapeLabel(opt: MagnetOption): string {
    return opt.shape === 'round' ? 'Round' : 'Rect';
  }

  magnetStandardLabel(opt: MagnetOption): string {
    if (!opt.standardMm) return 'no stocked size fits';
    if ('dia' in opt.standardMm) return `${opt.standardMm.dia} mm ⌀`;
    return `${opt.standardMm.length} × ${opt.standardMm.width} mm`;
  }

  magnetMaxLabel(opt: MagnetOption): string {
    if ('dia' in opt.fitsMm) return `max ${opt.fitsMm.dia.toFixed(1)} mm`;
    return `max ${opt.fitsMm.length.toFixed(1)} × ${opt.fitsMm.width.toFixed(1)} mm`;
  }

  /** Display label for a drive terminal, e.g. 'U+'. */
  driveLabel(t: TerminalPad): string {
    return `${this.phaseLabels[t.phase]}+`;
  }

  // ════════════════════════════════════════════════════════════════════════════
  //  GERBER GENERATION
  // ════════════════════════════════════════════════════════════════════════════

  /** Coil geometry for In1..B_Cu layers only; F_Cu bus routing lives in `busData`. */
  computeGerberData(): GerberData {
    const data = computeCoilGerberData(
      this.params, this.phaseLabels, this.coilShape, this.coilFlips,
      { padMm: this.termPadMm, holeMm: this.termHoleMm },
    );
    if (this.windingType === 'independent') {
      const termDims = { padMm: this.termPadMm, holeMm: this.termHoleMm };
      if (this.stackingTabsActive) {
        // Stacking-tabs: the phase leads terminate at the six edge ears instead of
        // adjacent return pads. Each ear's three plated joint holes join the
        // terminal set (role 'drive' for + ears, 'return' for − ears; ear labels
        // 'U+'/'U−'/…) so preview/role listings and the drill T3 group see them;
        // the `ear` flag routes them to the T3/1.0mm tool and to pads on BOTH faces.
        const earTerminals: TerminalPad[] = [];
        for (const ear of computeStackingEars(this.params, termDims)) {
          const role: TerminalPad['role'] = ear.polarity === 'plus' ? 'drive' : 'return';
          for (const [x, y] of ear.holes) {
            earTerminals.push({ x, y, phase: ear.phase, label: ear.label, role, ear: true });
          }
        }
        return { ...data, terminals: [...data.terminals, ...earTerminals] };
      }
      // 6-lead adjacent: the return (−) pads are F.Cu solder pads that join the entry-pad
      // terminal set so silkscreen/preview/role listings and the drill T1 group see
      // them. Their plated holes are the F.Cu↔slot-layer transitions; the slot-layer
      // pad is emitted by computeBusRouting.extraPadTerminals at identical coordinates.
      // Same `coilFlips` array as the drive terminals above (T1 coupling audit) —
      // a return pad can never derive a different flip state than its own phase's
      // drive pad.
      return {
        ...data,
        terminals: [
          ...data.terminals,
          ...computeReturnPads(this.params, termDims, this.coilFlips),
        ],
      };
    }
    return data;
  }

  // ── Gerber helpers ────────────────────────────────────────────────────────

  private gc(mm: number): string {
    return Math.round(mm * 1e6).toString();
  }

  private getLayerName(i: number): string {
    if (i === 0)                          return 'F_Cu';
    if (i === this.params.numLayers - 1)  return 'B_Cu';
    return `In${i}_Cu`;
  }

  /** Gerber X2 file attributes — how CAM tools (JLCPCB) learn each file's
   *  layer role regardless of filename. Placed before the FS declaration. */
  private x2Attributes(fileFunction: string, polarity: 'Positive' | 'Negative' = 'Positive'): string[] {
    return [
      '%TF.GenerationSoftware,steve-corry,pcb-stator,1.0*%',
      `%TF.FileFunction,${fileFunction}*%`,
      `%TF.FilePolarity,${polarity}*%`,
    ];
  }

  /** X2 FileFunction for copper layer i: Copper,L<n>,Top|Inr|Bot (KiCad convention). */
  private copperFileFunction(i: number): string {
    return copperFileFunction(i, this.params.numLayers);
  }

  /** Zip filenames use KiCad-Protel extensions — the naming JLCPCB's uploader
   *  keys on (gtl/g2/g3/gbl/gts/gbs/gto/gm1). The in-app layer labels and the
   *  DRC harness keep the long names; only the fab package is renamed. */
  fabFileName(kind: 'copper' | 'F_Mask' | 'B_Mask' | 'F_Silk' | 'Edge_Cuts' | 'drill' | 'NPTH', layerIndex = 0): string {
    const base = 'pcb-stator';
    switch (kind) {
      case 'copper':
        if (layerIndex === 0) return `${base}.gtl`;
        if (layerIndex === this.params.numLayers - 1) return `${base}.gbl`;
        return `${base}.g${layerIndex + 1}`;
      case 'F_Mask': return `${base}.gts`;
      case 'B_Mask': return `${base}.gbs`;
      case 'F_Silk': return `${base}.gto`;
      case 'Edge_Cuts': return `${base}.gm1`;
      case 'drill': return `${base}.drl`;
      case 'NPTH': return `${base}-NPTH.drl`;
    }
  }

  /** String-building bodies for these five methods now live in the
   *  board-agnostic `./pcb-stator-gerber` module (Task 2) — byte-identical
   *  to the pre-extraction code, pinned by `pcb-stator-gerber.spec.ts`'s
   *  golden SHA-256 hashes and the X2/JLCPCB-profile tests below. */

  generateGerberLayer(layerIndex: number, data: GerberData): string {
    const layerName = layerNameByIndex(layerIndex, this.params.numLayers);
    return renderCopperLayer(layerIndex, layerName, data, this.busData, this.params, this.VIA_PAD_MM, this.termPadMm);
  }

  /** Generate soldermask Gerber — openings over via and terminal pads. */
  generateSolderMask(layerName: string, data: GerberData): string {
    return renderSolderMask(layerName, data, this.busData, this.MASK_EXPANSION, this.VIA_PAD_MM, this.termPadMm);
  }

  /** Generate top-silkscreen Gerber — phase-drive letter labels (U/V/W…) beside each drive pad,
   *  plus (stacking-tabs mode) each ear's stage-mating registration tick + compound label. The
   *  ear marks come from `earSilkMarks`, which is already gated on `stackingTabsActive` (empty
   *  otherwise), so wye/delta/adjacent F_Silk output is byte-unchanged — additive only. */
  generateSilkscreen(_data: GerberData): string {
    const lines = [
      ...this.x2Attributes('Legend,Top'),
      'G04 PCB Stator - F_Silk (phase drive labels) *',
      '%FSLAX36Y36*%', '%MOMM*%', '%LPD*%',
      `%ADD10C,${SILK_STROKE_MM.toFixed(4)}*%`,
      'G01*', 'D10*',
    ];
    for (const label of this.silkLabels) {
      for (const s of label.segments) {
        lines.push(`X${this.gc(s.x1)}Y${this.gc(-s.y1)}D02*`);
        lines.push(`X${this.gc(s.x2)}Y${this.gc(-s.y2)}D01*`);
      }
    }
    for (const mark of this.earSilkMarks) {
      for (const s of [mark.tick, ...mark.labelSegments]) {
        lines.push(`X${this.gc(s.x1)}Y${this.gc(-s.y1)}D02*`);
        lines.push(`X${this.gc(s.x2)}Y${this.gc(-s.y2)}D01*`);
      }
    }
    lines.push('M02*');
    return lines.join('\n');
  }

  generateDrillFile(data: GerberData): string {
    return renderDrillFile(data, this.busData, this.termHoleMm);
  }

  /** NPTH (non-plated) mounting holes as a separate Excellon file. */
  generateMountingDrill(): string {
    return renderMountingDrill(this.mountingHoleDia, this.mountingHoleList);
  }

  generateEdgeCuts(): string {
    if (this.stackingTabsActive) {
      // Ears present → the outer profile is the eared spliced loop (one closed
      // simple loop; arcs flattened to polyline vertices), with the bore as a
      // sampled inner cutout. The circles path is left byte-untouched for every
      // other layout (including an invalid/forced stacking-tabs request, which
      // `stackingTabsActive` already excludes).
      const termDims = { padMm: this.termPadMm, holeMm: this.termHoleMm };
      const outline = buildOutlineWithEars(this.params, computeStackingEars(this.params, termDims));
      const bore = this.sampleBoreLoop(this.params.innerRadius);
      return renderGenericOutline(outline, 'PCB Stator', [bore]);
    }
    return renderEdgeCuts(this.params.outerRadius, this.params.innerRadius);
  }

  /** Closed polyline approximation of the inner bore circle, for the eared
   *  edge-cuts path (which renders straight polylines, not G03 arcs). 128
   *  segments matches the ear-outline sampling density; first vertex repeated. */
  private sampleBoreLoop(radius: number, samples = 128): Array<[number, number]> {
    const loop: Array<[number, number]> = [];
    for (let i = 0; i < samples; i++) {
      const a = (2 * Math.PI * i) / samples;
      loop.push([radius * Math.cos(a), radius * Math.sin(a)]);
    }
    loop.push(loop[0]);
    return loop;
  }

  /**
   * Board-agnostic descriptor of the stator board (Task 2) — for future
   * descriptive/preview consumers (e.g. a stack-preview UI or DRC pass), NOT
   * the source of the actual downloaded fab files (those stay on the
   * byte-stable `generate*` delegates above). `GerberDraw.points` has no
   * arc/curvature representation, so coil/bus arcs are flattened to their
   * straight start→end chord here, and `outline` is the board's OUTER
   * profile only (the interface can't express the stator's separate
   * inner-bore boundary).
   */
  get statorBoardDescriptor(): BoardDescriptor {
    const data = this.computeGerberData();
    const bus = this.busData;
    const numLayers = this.params.numLayers;

    const layers: BoardLayerSpec[] = [];
    for (let i = 0; i < numLayers; i++) {
      const layerName = layerNameByIndex(i, numLayers);
      const draws: GerberDraw[] = [];
      for (const s of bus.segments.filter(s => s.layer === layerName)) {
        draws.push({ apertureDiaMm: this.params.traceWidth, kind: 'polyline', points: [[s.startX, s.startY], [s.endX, s.endY]] });
      }
      for (const arc of bus.arcs.filter(a => a.layer === layerName)) {
        draws.push({ apertureDiaMm: this.params.traceWidth, kind: 'polyline', points: [[arc.startX, arc.startY], [arc.endX, arc.endY]] });
      }
      for (const t of data.turns.filter(t => t.layer === i)) {
        draws.push({ apertureDiaMm: this.params.traceWidth, kind: 'polyline', points: [[t.outerX, t.outerY], [t.innerX, t.innerY]] });
      }
      for (const arc of data.arcs.filter(a => a.layer === i)) {
        draws.push({ apertureDiaMm: this.params.traceWidth, kind: 'polyline', points: [[arc.startX, arc.startY], [arc.endX, arc.endY]] });
      }
      for (const cv of data.chainingVias) {
        if (cv.layers.includes(layerName)) draws.push({ apertureDiaMm: this.VIA_PAD_MM, kind: 'flash', points: [[cv.position.x, cv.position.y]] });
      }
      for (const bv of bus.bridgeVias) {
        if (bv.layers.includes(layerName)) draws.push({ apertureDiaMm: this.VIA_PAD_MM, kind: 'flash', points: [[bv.position.x, bv.position.y]] });
      }
      for (const ev of data.exitVias) {
        if (ev.layers.includes(layerName)) draws.push({ apertureDiaMm: this.VIA_PAD_MM, kind: 'flash', points: [[ev.position.x, ev.position.y]] });
      }
      for (const bp of bus.extraPadTerminals.filter(bp => bp.layer === layerName)) {
        draws.push({ apertureDiaMm: bp.padClass === 'via' ? this.VIA_PAD_MM : this.termPadMm, kind: 'flash', points: [[bp.x, bp.y]] });
      }
      if (layerName === 'F.Cu') {
        for (const term of data.terminals) draws.push({ apertureDiaMm: this.termPadMm, kind: 'flash', points: [[term.x, term.y]] });
      }
      layers.push({
        name: layerName.replace('.', '_'),
        fileFunction: this.copperFileFunction(i),
        polarity: 'Positive',
        draws,
      });
    }

    for (const [maskName, cuLayer] of [['F_Mask', 'F.Cu'], ['B_Mask', 'B.Cu']] as const) {
      const draws: GerberDraw[] = [];
      for (const cv of data.chainingVias) {
        if (cv.layers.includes(cuLayer)) draws.push({ apertureDiaMm: this.VIA_PAD_MM + this.MASK_EXPANSION * 2, kind: 'flash', points: [[cv.position.x, cv.position.y]] });
      }
      for (const bv of bus.bridgeVias) {
        if (bv.layers.includes(cuLayer)) draws.push({ apertureDiaMm: this.VIA_PAD_MM + this.MASK_EXPANSION * 2, kind: 'flash', points: [[bv.position.x, bv.position.y]] });
      }
      for (const ev of data.exitVias) {
        if (ev.layers.includes(cuLayer)) draws.push({ apertureDiaMm: this.VIA_PAD_MM + this.MASK_EXPANSION * 2, kind: 'flash', points: [[ev.position.x, ev.position.y]] });
      }
      if (cuLayer === 'F.Cu') {
        for (const t of data.terminals) draws.push({ apertureDiaMm: this.termPadMm + this.MASK_EXPANSION * 2, kind: 'flash', points: [[t.x, t.y]] });
      }
      for (const bp of bus.extraPadTerminals.filter(bp => bp.layer === cuLayer && bp.padClass === 'terminal')) {
        draws.push({ apertureDiaMm: this.termPadMm + this.MASK_EXPANSION * 2, kind: 'flash', points: [[bp.x, bp.y]] });
      }
      layers.push({
        name: maskName,
        fileFunction: `Soldermask,${maskName === 'F_Mask' ? 'Top' : 'Bot'}`,
        polarity: 'Negative',
        draws,
      });
    }

    const silkDraws: GerberDraw[] = [];
    for (const label of this.silkLabels) {
      for (const s of label.segments) {
        silkDraws.push({ apertureDiaMm: SILK_STROKE_MM, kind: 'polyline', points: [[s.x1, s.y1], [s.x2, s.y2]] });
      }
    }
    layers.push({ name: 'F_Silk', fileFunction: 'Legend,Top', polarity: 'Positive', draws: silkDraws });

    const N = 64;
    const outline: Array<[number, number]> = Array.from({ length: N }, (_, i) => {
      const a = (i / N) * 2 * Math.PI;
      return [this.params.outerRadius * Math.cos(a), this.params.outerRadius * Math.sin(a)];
    });

    const dedupe = (pts: Array<[number, number]>): Array<[number, number]> => {
      const seen = new Set<string>();
      return pts.filter(([x, y]) => {
        const key = `${x.toFixed(3)},${y.toFixed(3)}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    };

    const drills: DrillSpec[] = [
      { toolDiaMm: this.termHoleMm, plated: true, holes: dedupe(data.terminals.map(t => [t.x, t.y])) },
      {
        toolDiaMm: 0.4, plated: true,
        holes: dedupe([
          ...data.chainingVias.map(cv => [cv.position.x, cv.position.y] as [number, number]),
          ...bus.bridgeVias.map(bv => [bv.position.x, bv.position.y] as [number, number]),
          ...data.exitVias.map(ev => [ev.position.x, ev.position.y] as [number, number]),
        ]),
      },
    ];
    if (this.mountingHoles > 0) {
      drills.push({ toolDiaMm: this.mountingHoleDia, plated: false, holes: this.mountingHoleList.map(h => [h.x, h.y]) });
    }

    return {
      boardName: 'pcb-stator',
      outline,
      layers,
      drills,
      x2CopperNames: layers.filter(l => l.fileFunction.startsWith('Copper,')).map(l => l.fileFunction),
    };
  }

  async downloadGerbers(): Promise<void> {
    this.isGenerating = true;
    try {
      const { default: JSZip } = await import('jszip');
      const zip  = new JSZip();
      const data = this.computeGerberData();
      const name = 'pcb-stator';

      for (let l = 0; l < this.params.numLayers; l++) {
        zip.file(this.fabFileName('copper', l), this.generateGerberLayer(l, data));
      }
      zip.file(this.fabFileName('Edge_Cuts'), this.generateEdgeCuts());
      zip.file(this.fabFileName('F_Mask'), this.generateSolderMask('F_Mask', data));
      zip.file(this.fabFileName('B_Mask'), this.generateSolderMask('B_Mask', data));
      zip.file(this.fabFileName('F_Silk'), this.generateSilkscreen(data));
      zip.file(this.fabFileName('drill'), this.generateDrillFile(data));
      if (this.mountingHoles > 0) {
        zip.file(this.fabFileName('NPTH'), this.generateMountingDrill());
      }
      zip.file('README.txt', this.generateReadme(data));

      const blob = await zip.generateAsync({ type: 'blob' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${name}-gerbers.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      this.isGenerating = false;
    }
  }

  downloadRotorStl(): void {
    const blob = new Blob([this.rotorStlBytes], { type: 'application/octet-stream' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'pcb-stator-rotor.stl';
    a.click();
    URL.revokeObjectURL(url);
  }

  private triggerBlobDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a   = document.createElement('a');
    a.href    = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  /** Downloads never block (house rule) — this awaits the solver if it has
   *  not run yet for the current params, same as clicking into the tab would. */
  async downloadEncoderRing(): Promise<void> {
    this.isGenerating = true;
    try {
      const result = await this.ensureEncoderBoard();
      const { default: JSZip } = await import('jszip');
      const zip = new JSZip();
      for (const f of emitBoard(result.descriptor)) zip.file(f.filename, f.content);
      zip.file('README.txt', this.generateAssemblyReadme());
      const blob = await zip.generateAsync({ type: 'blob' });
      this.triggerBlobDownload(blob, 'pcb-stator-encoder-ring.zip');
    } finally {
      this.isGenerating = false;
    }
  }

  async downloadTargetRing(): Promise<void> {
    this.isGenerating = true;
    try {
      const desc = buildTargetBoardDescriptor(this.targetRing);
      const { default: JSZip } = await import('jszip');
      const zip = new JSZip();
      for (const f of emitBoard(desc)) zip.file(f.filename, f.content);
      const blob = await zip.generateAsync({ type: 'blob' });
      this.triggerBlobDownload(blob, 'pcb-stator-encoder-target.zip');
    } finally {
      this.isGenerating = false;
    }
  }

  downloadAssemblyReadme(): void {
    const blob = new Blob([this.generateAssemblyReadme()], { type: 'text/plain' });
    this.triggerBlobDownload(blob, 'pcb-stator-encoder-assembly-README.txt');
  }

  /** Stack-assembly notes: stack order, the standoff/spacer length math, the
   *  phase-offset + mechanical key-azimuth constants (0deg electrical by
   *  construction, both generators share ENCODER_DATUM_RAD; the key tab's
   *  angle is a SEPARATE mechanical constant that guarantees the electrical
   *  constant survives assembly), chip config incl. a VDDD isolation warning,
   *  and a BOM incl. a live stock warning + the eval-kit pointer. Bundled into
   *  the encoder-ring zip and separately downloadable. */
  generateAssemblyReadme(): string {
    const b = this.encoderBands;
    const rl = this.rotorLayout;
    const keyRad = this.targetRing.keyTabAngleRad;
    const keyDeg = keyRad * 180 / Math.PI;
    const recessDepthMm = rl.recess?.depthMm ?? (this.encoderParams.targetThicknessMm + RECESS_GLUE_MM);
    const belowFlushMm = recessDepthMm - this.encoderParams.targetThicknessMm;
    const discOdMm = b.valid ? b.fine.rOutMm + DISC_RIM_MM : 0;
    const seatLine = rl.recess !== undefined
      ? `  The target disc (OD R${discOdMm.toFixed(2)} mm) seats FULLY inside the pocket-clamped`
        + ` counterbore wall (R${rl.recess.rOutMm.toFixed(2)} mm) with ${RECESS_FIT_MM.toFixed(2)} mm slip-fit`
        + ' clearance — the disc OD is ruled off the outer RX track (fine.rOut + rim), NOT the TX loop,'
        + ' so it clears the through-pocket band and no rim rests proud of the recess lip.'
        + ` The recess floor spans R${rl.recess.rInMm.toFixed(2)}–${rl.recess.rOutMm.toFixed(2)} mm — under the`
        + ' WHOLE substrate annulus (inner wall follows the target bore, not the copper), so the disc'
        + ` sinks the full ${rl.recess.depthMm.toFixed(2)} mm to the floor with no inner land under it.`
      : '  Target seat: n/a (enable the encoder feature with mounting holes on to compute it).';
    const txNH = this.sensingElement.txInductanceEstNH;
    const txOutOfWindow = txNH < CHIP.txInductanceMinNH || txNH > CHIP.txInductanceMaxNH;

    const lines: string[] = [
      'PCB Stator — Encoder assembly notes',
      '='.repeat(40),
      '',
      'STACK ORDER (axial, sensor side of the stator toward the rotor)',
      '─'.repeat(40),
      '  1. Stator board.',
      '  2. Motor air gap (per motor design — external input, not computed by this tool).',
      '  3. Rotor disc: magnets on the stator-facing front; keyed back-face recess on the back.',
      '  4. Target ring (single-sided copper disc): glues into the recess, copper face',
      '     toward the encoder ring.',
      `  5. Sensing gap : ${this.encoderParams.sensingGapMm.toFixed(2)} mm (sensingGapMm) between the`,
      '     target-ring copper face and the encoder-ring coil face.',
      '  6. Encoder-ring board (2-layer): coil face ("down"/B.Cu) faces the rotor;',
      '     component face ("up"/F.Cu) faces away, clear of the sensing gap.',
      '  The stator board and the encoder-ring board share ONE M3 bolt circle; standoffs',
      '  span the full axial stack between them (see SPACER / STANDOFF MATH below).',
      '',
      'SPACER / STANDOFF MATH',
      '─'.repeat(40),
      '  standoffLengthMm = motorAirGapMm + rotorDiscThicknessMm + sensingGapMm',
      '    motorAirGapMm        : per motor design (external input, not computed here)',
      `    rotorDiscThicknessMm : ${this.rotorDiscThicknessMm.toFixed(2)} mm (this config)`,
      `    sensingGapMm         : ${this.encoderParams.sensingGapMm.toFixed(2)} mm (encoderParams default)`,
      '  sensingGapMm is measured face-to-face (target copper -> encoder coil), so it',
      '  already absorbs the target\'s recess seating — no separate correction term:',
      `    recess depth = targetThicknessMm (${this.encoderParams.targetThicknessMm.toFixed(2)} mm) + glue allowance`,
      `                   (${RECESS_GLUE_MM.toFixed(2)} mm) = ${recessDepthMm.toFixed(2)} mm`,
      `    -> the target's copper face sits between flush and ${belowFlushMm.toFixed(2)} mm BELOW the rotor's`,
      '       back-face plane (never proud) — inside typical standoff/shim tolerance.',
      '',
      'PHASE-OFFSET / KEY-AZIMUTH CONSTANTS',
      '─'.repeat(40),
      '  Sensor-zero <-> phase-A electrical offset = 0 deg by construction: the coil',
      '  generator and the target-ring generator both derive their pattern from the SAME',
      '  angular datum (ENCODER_DATUM_RAD = -pi/2), which also matches the rotor\'s pole-0',
      '  index notch — no correction term is needed in firmware/config.',
      '',
      '  Key azimuth (mechanical — a SEPARATE constant that keeps the target from being',
      '  glued in at the wrong phase, protecting the electrical constant above):',
      '    keyAzimuthRad = datumRad + pi / (2 * finePeriods)',
      `                  = ${this.ENCODER_DATUM_RAD.toFixed(4)} + pi / (2 * ${this.encoderPeriods.fine})`,
      `                  = ${keyRad.toFixed(4)} rad = ${keyDeg.toFixed(1)} deg`,
      '  Half a pole pitch off the datum — the middle of the solid web between adjacent',
      '  magnet pockets, deliberately NOT on the datum itself (a tab there would land over',
      '  pocket 0, where the rotor has no material to key into). The rotor\'s recess key',
      '  finger derives its angle from this SAME formula, so the target cannot glue in rotated',
      '  — the keying is structural, not just documented.',
      seatLine,
      '',
      'CHIP CONFIGURATION',
      '─'.repeat(40),
      `  Part          : ${CHIP.partNumber} (${CHIP.package})`,
      `  Supply        : ${CHIP.supplyVolts} V`,
      `  Fine track    : ${this.encoderPeriods.fine} periods/rev`,
      `  Coarse track  : ${this.encoderPeriods.coarse} period/rev (absolute reference)`,
      `  Output modes  : ${CHIP.outputModes.join(', ')}`,
      `  TX carrier    : ${(CHIP.carrierHz / 1e6).toFixed(2)} MHz nominal, tank cap ${CHIP.txTankCapPF} pF`,
      `  TX inductance : est. ${txNH.toFixed(0)} nH (chip window ${CHIP.txInductanceMinNH}-${CHIP.txInductanceMaxNH} nH)`,
      txOutOfWindow
        ? '  CAUTION: the analytic estimate above falls OUTSIDE the chip\'s usable LC-tank'
          + ' window — cross-check against the Renesas Coil Optimizer before ordering parts'
          + ' (docs/research/2026-07-12-encoder-optimizer-crosscheck.md) and revisit the TX'
          + ' turn count/geometry if the optimizer confirms it.'
        : '',
      '',
      'VDDD ISOLATION WARNING',
      '─'.repeat(40),
      '  Pin 16 (VDDD) is an INTERNALLY-REGULATED digital-supply rail — electrically',
      '  distinct from pin 14 (VDD) even though both carry the same net name in the',
      '  schematic. VDDD needs its OWN dedicated 100 nF cap to GND (C4).',
      '  DO NOT strap pin 16 to the pin-14 VDD copper or cap (C3): the board routes them',
      '  as two galvanically isolated islands on purpose; bridging them defeats the chip\'s',
      '  internal regulator and is an out-of-spec assembly error.',
      '',
      'SENSING BANDS',
      '─'.repeat(40),
      b.valid
        ? [
            `  Coarse track : R${b.coarse.rInMm.toFixed(2)}-${b.coarse.rOutMm.toFixed(2)} mm`,
            `  Fine track   : R${b.fine.rInMm.toFixed(2)}-${b.fine.rOutMm.toFixed(2)} mm`,
            `  TX allocation: R${b.tx.rInMm.toFixed(2)}-${b.tx.rOutMm.toFixed(2)} mm`,
            `  Hub keep-away: R${b.hubKeepAwayMm.toFixed(2)} mm`,
          ].join('\n')
        : `  INVALID: ${b.reasons.join(' ')}`,
      '',
      'BOM',
      '─'.repeat(40),
      `  U1  ${CHIP.partNumber} (${CHIP.package})`,
      '      STOCK WARNING: 0 in stock at Digi-Key (checked 2026-07-09), ~$177/unit,',
      '      32-week factory lead on backorder. See the eval kit line below for a',
      '      bundled-IC alternative to bring the design up while U1 is on order.',
      `  C1  ${(CHIP.txTankCapPF / 1000).toFixed(1)} nF tank cap (CTx1), TX1 to GND`,
      `  C2  ${(CHIP.txTankCapPF / 1000).toFixed(1)} nF tank cap (CTx2), TX2 to GND (split pair, carrier`
        + ` ~${(CHIP.carrierHz / 1e6).toFixed(1)} MHz)`,
      '  C3  100 nF decoupling, VDD (pin 14) to GND',
      '  C4  100 nF decoupling, VDDD (pin 16, isolated island — see warning above) to GND',
      '  J1  2.54mm 5-pin header: OUT_A, OUT_B, OUT_I, VDD, GND',
      '  4x M3 standoffs (threaded spacers) on the shared bolt circle — see SPACER /',
      '      STANDOFF MATH for the length.',
      '  Target-ring glue: any thin structural adhesive rated for the disc\'s',
      `                     ${this.encoderParams.targetThicknessMm.toFixed(2)} mm copper-clad thickness.`,
      `  Eval kit: RTKA2P3226S00000BE (${CHIP.partNumber} reference design; bundles the IC —`,
      '            a stock workaround for bring-up while U1 is on backorder).',
      '',
    ];
    return lines.join('\n');
  }

  private generateReadme(data: GerberData): string {
    const { numPhases, numPoles, numCoils, numLayers,
            innerRadius, outerRadius, traceWidth, traceClearance,
            outerMargin, innerMargin } = this.params;
    const numEntries = data.terminals.length;
    const numExits = data.exitVias.length;
    const numChain = data.chainingVias.length;
    const numBridge = this.busData.bridgeVias.length;
    // Task 4b: exit vias are via-class (T2), no longer terminal-class (T1).
    const totalT1 = numEntries;
    const totalT2 = numChain + numBridge + numExits;
    const numPairs = numLayers / 2;
    // Pair-loop layer labels: L0..L{numLayers-1}, mapped through getLayerName
    // for the parenthetical KiCad name (F_Cu/In*_Cu/B_Cu → F.Cu/In*.Cu/B.Cu).
    const lName = (i: number) => this.getLayerName(i).replace('_', '.');
    const last = numLayers - 1;

    const layerSchemeLines: string[] = [];
    for (let j = 0; j < numPairs; j++) {
      const a = 2 * j, b = 2 * j + 1;
      const aLabel = `L${a} (${lName(a)})`.padEnd(12);
      if (j === 0) {
        layerSchemeLines.push(`  ${aLabel}— forward spiral (outer→inner).`);
        layerSchemeLines.push(`  L${b} (${lName(b)}) — MIRRORED reverse spiral (inner→outer): the reflection of the`);
        layerSchemeLines.push('                forward path about the coil centerline, so current keeps');
        layerSchemeLines.push('                circulating the SAME rotational direction while exiting outward.');
      } else if (j === numPairs - 1) {
        layerSchemeLines.push(`  ${aLabel}— forward spiral again (enters via an outer-margin hookup arc).`);
        layerSchemeLines.push(`  L${b} (${lName(b)}) — mirrored truncated reverse spiral, ending at the inner margin.`);
      } else {
        layerSchemeLines.push(`  ${aLabel}— forward spiral again (enters via an outer-margin hookup arc).`);
        layerSchemeLines.push(`  L${b} (${lName(b)}) — MIRRORED reverse spiral (co-rotating), returning to the outer margin.`);
      }
    }

    const layerCountWord = numLayers === 4 ? 'four' : String(numLayers);

    // Current-path narration per layer boundary (chaining vias), one entry per
    // pair, generalizing the original 4-layer (2-pair) walk: j=0 is the
    // forward spiral + first chaining via + mirrored return leg (verbatim at
    // N=4); the last pair is the truncated mirrored return to the exit via
    // (verbatim at N=4); any middle pairs (N=6/8) repeat the "forward again /
    // mirrored return" hookup-arc pattern used by the pre-last pair today.
    const currentPathLines: string[] = [];
    currentPathLines.push('      → L0 forward spiral, outer→inner');
    currentPathLines.push('      → L0↔L1 chaining via (T2, 0.4 mm drill / 0.6 mm pad, F.Cu+In1 pads)');
    currentPathLines.push('      → L1 MIRRORED reverse spiral, inner→outer (co-rotating with L0)');
    for (let j = 1; j < numPairs; j++) {
      const a = 2 * j, b = 2 * j + 1;
      const prev = a - 1;
      currentPathLines.push(`      → L${prev} tail (${lName(prev)}) to the L${prev}↔L${a} via at outer-right of turn 0`);
      currentPathLines.push(`      → L${prev}↔L${a} chaining via (T2, ${lName(prev)}+${lName(a)} pads)`);
      currentPathLines.push(`      → L${a} outer-margin hookup arc (${lName(a)}) back to outer-left of turn 0`);
      currentPathLines.push(`      → L${a} forward spiral, stopped early at this pair's rung angle (drill keep-away`);
      currentPathLines.push('        from the L0↔L1 hole, where an untruncated inner arc would land)');
      currentPathLines.push(`      → L${a}↔L${b} chaining via (T2, ${lName(a)}+${lName(b)} pads)`);
      if (j === 1) {
        currentPathLines.push('        (wedge shape only: both inner chaining vias relocate radially to the');
        currentPathLines.push('        inner-margin ring shared with the exit via — see EXIT TERMINALS)');
      }
      if (j === numPairs - 1) {
        currentPathLines.push(`      → L${b} MIRRORED truncated reverse spiral (co-rotating), ending mid-turn-0`);
        currentPathLines.push(`      → L${b} tail (${lName(b)}), inset inward, to the exit via`);
      } else {
        currentPathLines.push(`      → L${b} MIRRORED reverse spiral (co-rotating with L${a})`);
      }
    }

    const lines: string[] = [
      'PCB Stator — Gerber package (mirrored-layers series chain)',
      '='.repeat(40),
      '',
      `Coils              : ${numCoils}`,
      `Phases             : ${numPhases}   (${this.windingType.toUpperCase()} connection)`,
      `Magnet poles (ref) : ${numPoles}`,
      `Turns / layer      : ${this.turnsPerLayer}`,
      `Total layers       : ${numLayers} (${numLayers}-layer series chain, ${numPairs} co-rotating pairs)`,
      `Board outer        : ${outerRadius} mm   (coil outer = ${outerRadius - outerMargin} mm)`,
      `Board inner        : ${innerRadius} mm   (coil inner = ${innerRadius + innerMargin} mm)`,
      `Outer margin       : ${outerMargin} mm`,
      `Inner margin       : ${innerMargin} mm`,
      `Trace width        : ${traceWidth} mm`,
      `Clearance          : ${traceClearance} mm`,
      `Coils / phase      : ${this.coilsPerPhase}`,
      '',
      'LAYER SCHEME (mirrored co-rotating series chain)',
      '─'.repeat(40),
      '',
      ...layerSchemeLines,
      '',
      `  All ${layerCountWord} layers CO-ROTATE (verified by signed-moment tests: net coil MMF`,
      '  ≥ 3.8× a single layer). Total MMF ≈ numTurnsPerLayer × numLayers × I.',
      '',
      '  COIL POLARITY: coils marked (mirrored) below are built as the mirror image',
      '  of the base coil so every coil\'s EMF adds inside its phase for this pole',
      `  count. Winding factor: ${this.windingFactors.map(w => w.toFixed(2)).join(' / ')}.`,
      '',
      'WINDING MODEL',
      '─'.repeat(40),
      '',
      `Trapezoidal nested spiral, ${numLayers}-layer mirrored series chain:`,
      `  Each coil occupies a ${this.sectorAngleDeg.toFixed(1)}° sector.`,
      '  Current path per coil:',
      '    entry pad (F.Cu, outer margin) — solder terminal for drive/internal-link coils',
      ...currentPathLines,
      '      → EXIT VIA (T2, via-class internal junction — NOT a solder terminal)',
      '      → F.Cu inner-margin bus to the next same-phase coil (series chain), or',
      `        to ${this.windingType === 'independent' ? 'its own U−/V−/W− return pad (independent mode: no board bus)' : 'the B.Cu outer bus (wye neutral / delta cycle)'} at the chain ends (${numLayers - 1} chaining vias per coil).`,
      '',
      `  Turns / coil (auto): ${this.turnsPerLayer}`,
      `  Radial limit       : ${this.turnLimits.radial}`,
      `  Angular limit      : ${this.turnLimits.angular}`,
      '',
      '  Each re-entrant forward layer (L2, and L4/L6/... on 6/8-layer boards — one per',
      '  pair after the first) gives up a small fraction of its final turn to the rung',
      '  keep-away (drill clearance from the L0↔L1 via); every other turn on every other',
      `  layer runs full, on all ${layerCountWord} layers.`,
      '',
      'EXIT TERMINALS (Task 4b)',
      '─'.repeat(40),
      '',
      `  Each coil's L${last} tail ends at an EXIT VIA, not a solder pad: a via-class`,
      '  internal junction (tool 0.4 mm, pad 0.6 mm) sitting in the empty annulus',
      '  between the innermost coil arc and the top inner-bus slot. It is inset',
      '  angularly inward (toward the coil interior) so its pad clears both the',
      '  inner bus slot ladder and a mirrored neighbor\'s exit across the coil',
      '  boundary. Exit vias drill in the T2 (0.4 mm) group alongside chaining vias —',
      '  they carry no B.Cu solder terminal of their own; the series chain and the',
      '  wye/delta bus connect to them purely through copper, not through a pad a',
      '  technician would solder to.',
      '',
      'BUS ROUTING (multi-layer)',
      '─'.repeat(40),
      '',
      '  Inner margin (intra-phase series-chain joins): each join sits on a fixed-',
      '  pitch slot ladder (one slot per phase, plus generalized extra slots for',
      '  k > 2 coils/phase) and stays on F.Cu, bridged to its home layer by two',
      '  BRIDGE VIAS (T2, F.Cu + home-layer pads) at the arc endpoints:',
      '',
      '    Phase 0 (U) → F.Cu   (no bridge — already home)',
      '    Phase 1 (V) → In1.Cu',
      '    Phase 2 (W) → In2.Cu',
      '',
      '  Outer margin (Wye neutral / Delta cycle bus): the ENTIRE outer bus — arcs',
      '  AND their pad-to-arc segments — lives on B.Cu. This annulus is empty in the',
      '  mirrored chain (no coil geometry uses it), so the bus needs no dedicated',
      '  bridge vias: participating entry holes (wye far-ends; delta far-ends +',
      '  next-phase drive entries) simply carry a SELECTIVE B.Cu PAD in addition to',
      '  their normal F.Cu solder pad — the plated hole itself is the F.Cu↔B.Cu',
      '  layer transition. There are NO outer bridge vias in this scheme.',
      '',
    ];

    if (this.windingType === 'wye') {
      lines.push('  Wye (Y) outer bus:');
      lines.push('    All phase-chain far ends converge on a single neutral node via B.Cu');
      lines.push('    radial spokes. Drive terminals (phase inputs): each phase\'s first coil.');
    } else if (this.windingType === 'independent') {
      lines.push('  Independent (6-lead) outer bus: NONE.');
      lines.push('    Each phase is a fully isolated 2-terminal winding — no wye neutral, no');
      lines.push('    delta cycle, no inter-phase copper anywhere on this board. Each phase\'s');
      lines.push('    far end routes to its own U−/V−/W− return pad (role \'return\') instead of');
      lines.push('    an outer bus. See STACKING GUIDE below for how to wire it up.');
    } else {
      lines.push('  Delta (Δ) outer bus:');
      lines.push('    Three B.Cu cycle arcs link each phase\'s far end to the next phase\'s');
      lines.push('    drive entry. Drive taps at the three cycle junction points.');
      lines.push('    (Delta requires an even coils-per-phase; odd k emits no delta bus —');
      lines.push('    switch to Wye or pick an even multiple.)');
    }

    lines.push('');
    lines.push('DRILL FILE (two tools)');
    lines.push('─'.repeat(40));
    lines.push('');
    lines.push(`  T1 (0.800 mm): ${totalT1} holes  — entry/solder pads (${numEntries})`);
    lines.push(`  T2 (0.400 mm): ${totalT2} holes  — chaining vias (${numChain}) + bridge vias (${numBridge}) + exit vias (${numExits})`);
    lines.push(`  Total        : ${totalT1 + totalT2} plated through-holes`);
    lines.push('');
    lines.push('  Selective pads on each via control which copper layers participate. The fab');
    lines.push('  uses standard plated through-hole + selective-pad practice (no blind/buried');
    lines.push(`  vias). All holes drill all ${layerCountWord} layers; the via barrel only bonds to layers`);
    lines.push('  that have a pad. Exit vias are via-class (T2) internal junctions — Task 4b');
    lines.push('  moved them out of the T1 solder-terminal group.');
    lines.push('');

    lines.push('FILES');
    lines.push('─'.repeat(40));
    for (let l = 0; l < numLayers; l++) {
      lines.push(`  ${this.fabFileName('copper', l)}  (${this.getLayerName(l).replace('_', '.')})`);
    }
    lines.push('  pcb-stator.gm1  (board outline)');
    lines.push('  pcb-stator.gts  (top soldermask)');
    lines.push('  pcb-stator.gbs  (bottom soldermask)');
    lines.push('  pcb-stator.gto  (top silkscreen)');
    lines.push('  pcb-stator.drl  (plated holes)');
    lines.push('');
    lines.push('  Top silkscreen: phase-drive labels U/V/W… beside each drive pad.');
    lines.push('');
    lines.push('Rotor: a matching magnet-rotor model is a separate download in the Rotor panel — a ready-to-print/CAD binary STL mesh (pcb-stator-rotor.stl).');
    lines.push('');

    lines.push('COIL-TO-PHASE MAPPING');
    lines.push('─'.repeat(40));
    const plan = planCoilPolarities(this.params);
    for (let ph = 0; ph < numPhases; ph++) {
      const label = this.phaseLabels[ph];
      const coils = Array.from({ length: this.coilsPerPhase }, (_, k) => ph + k * numPhases);
      lines.push(`Phase ${label}: coils ${coils.map(cc => plan.flip[cc] ? `${cc} (mirrored)` : `${cc}`).join(', ')}`);
    }
    lines.push('');

    lines.push('MOTOR TERMINALS');
    lines.push('─'.repeat(40));
    for (const t of data.terminals.filter(t => t.role === 'drive')) {
      lines.push(`  ${this.phaseLabels[t.phase]}+  drive  @ (${t.x.toFixed(1)}, ${t.y.toFixed(1)}) mm`);
    }
    if (this.windingType === 'wye') {
      const nn = this.busData.neutralNode;
      if (nn) lines.push(`  N   neutral @ (${nn.x.toFixed(1)}, ${nn.y.toFixed(1)}) mm  (the *2+ free-entry pads all tie here)`);
      lines.push('  Drive U+/V+/W+ sit between each phase and neutral. The remaining labeled');
      lines.push('  holes are internal series-link junctions / bus taps, NOT motor terminals.');
    } else if (this.windingType === 'independent') {
      for (const t of data.terminals.filter(t => t.role === 'return')) {
        lines.push(`  ${this.phaseLabels[t.phase]}−  return @ (${t.x.toFixed(1)}, ${t.y.toFixed(1)}) mm`);
      }
      lines.push('  Independent (6-lead): U+/V+/W+ and U−/V−/W− above are all six REAL motor');
      lines.push('  terminals — every phase is a fully isolated 2-terminal winding with no');
      lines.push('  board-side bus. Wire them per the STACKING GUIDE below.');
    } else {
      lines.push('  Delta: U+/V+/W+ are the three drive taps (no neutral). The remaining labeled');
      lines.push('  holes are internal series-link junctions / bus taps, NOT motor terminals.');
    }
    lines.push('');

    lines.push('PHASE 3 NOTES');
    lines.push('─'.repeat(40));
    lines.push('  - Star-of-slots pole-aware phase mapping for arbitrary numPoles.');
    lines.push('  - Generalised k > 2 coils-per-phase chaining.');
    lines.push('  - Configurable chaining for non-4L stacks.');
    lines.push('  - Microvia / blind / buried via support (cost-justified configs).');

    if (this.mountingHoles > 0) {
      const keepout = this.mountingHoleDia / 2 + this.params.traceClearance;
      lines.push('');
      lines.push('MOUNTING HOLES (NPTH)');
      lines.push('─'.repeat(40));
      lines.push(`  Count            : ${this.mountingHoles}`);
      lines.push(`  Screw size       : ${this.mountingScrew}`);
      lines.push(`  Hole diameter    : ${this.mountingHoleDia.toFixed(2)} mm`);
      lines.push(`  Bolt-circle R    : ${this.mountingBoltRadius.toFixed(2)} mm`);
      lines.push(`  Copper keepout R : ${keepout.toFixed(2)} mm (from hole center)`);
      lines.push('  Non-plated, mechanical only — see pcb-stator-NPTH.drl.');
    }

    if (this.windingType === 'independent') {
      lines.push(...this.generateStackingGuide());
      if (this.terminalLayout === 'stacking-tabs') {
        lines.push(...this.generateStackingTabsGuide());
      }
    }

    return lines.join('\n');
  }

  /**
   * README §"STACKING GUIDE" — independent (6-lead) mode only. Every phase is
   * a fully isolated 2-terminal winding with no board-side wye/delta bus, so
   * the electrical topology is entirely an assembly-time choice: this section
   * documents the single-board jumper recipes, the multi-stage stacking
   * recipes, the stage-parity rule those recipes need once you stack more
   * than one board, and a clocking recommendation computed from THIS board's
   * live pole count and mounting-hole bolt circle (spec §3,
   * docs/superpowers/specs/2026-07-15-pcb-stator-six-lead-design.md).
   */
  private generateStackingGuide(): string[] {
    const lines: string[] = [];
    lines.push('');
    lines.push('STACKING GUIDE (6-lead / independent mode)');
    lines.push('─'.repeat(40));
    lines.push('');
    lines.push('  Every phase is a fully isolated 2-terminal winding (drive + / return −,');
    lines.push('  no internal wye or delta bus) — the electrical topology is chosen entirely');
    lines.push('  at assembly time, whether wiring one board or stacking several.');
    lines.push('');

    lines.push('  Single-board recipes:');
    lines.push('    External wye   : tie U−, V−, and W− together; drive from U+/V+/W+.');
    lines.push('    External delta : U− → V+, V− → W+, W− → U+ (closes the loop through the');
    lines.push('                     drive taps). Same triplen-harmonic caution as the built-in');
    lines.push('                     Delta mode — closing the loop lets any 3rd-harmonic (and');
    lines.push('                     other triplen) content circulate as current around it.');
    lines.push('');

    lines.push('  Multi-stage recipes (N boards stacked on a common shaft):');
    lines.push('    Series (× N)  : U1− → U2+, U2− → U3+, … chain same-phase leads stage to');
    lines.push('                    stage; drive from stage 1\'s +\'s and the last stage\'s −\'s.');
    lines.push('                    Volts add per stage (× N); amps unchanged. Best low-RPM');
    lines.push('                    alternator cut-in — the rectifier diode drop is amortized');
    lines.push('                    over N stages\' worth of volts.');
    lines.push('    AC-parallel   : tie same-phase leads across every stage (U1+ ‖ U2+ ‖ …,');
    lines.push('                    etc). Volts unchanged; amps add (× N). Requires the stages');
    lines.push('                    to be phase- and amplitude-matched — the least forgiving');
    lines.push('                    option; a mismatch drives circulating current between');
    lines.push('                    stages.');
    lines.push('    Per-stage rectifiers → shared DC bus : rectify each stage\'s three phases');
    lines.push('                    locally (its own 3-phase bridge, roughly $1 each) and tie the');
    lines.push('                    DC outputs onto a shared bus. No phase-matching requirement');
    lines.push('                    between stages — the sloppy-assembly-tolerant fallback.');
    lines.push('');

    lines.push('  STAGE PARITY (2–4 stages):');
    lines.push('    Adjacent stages sit across alternating air gaps and see OPPOSITE flux');
    lines.push('    polarity — every phase\'s EMF is NEGATED (180° shifted), not relabeled. A');
    lines.push('    bare V↔W swap does NOT fix this: no relabeling of a balanced 3-phase set');
    lines.push('    equals its negation, so in a series U-chain it leaves U1 + (−U1) = 0, a');
    lines.push('    dead phase.');
    lines.push('');
    lines.push('    This assumes the rotor stack presents opposite pole faces to successive');
    lines.push('    gaps (the common back-iron convention). A through-magnetized, aligned');
    lines.push('    stack presents the SAME sign to every gap — no parity correction is needed');
    lines.push('    there. Check your rotor stack\'s magnetization before wiring (the rotor-v2');
    lines.push('    stack builder will pin this convention explicitly).');
    lines.push('');
    if (this.stackingTabsActive) {
      // Mirror pointer for Finding 2 (final review): only printed when the
      // STACKING-TABS JOINT GUIDE section actually follows below, so this
      // section stays byte-stable ('STACKING-TABS' absent) for every other
      // terminalLayout/winding combination.
      lines.push('    This section does NOT apply to the touching stacking-tabs stack below (see');
      lines.push('    STACKING-TABS JOINT GUIDE) — boards edge-mated flush, face-to-face all');
      lines.push('    share one gap and need no parity correction.');
      lines.push('');
    }
    lines.push('      Stage  Wiring');
    lines.push('      1      normal (reference)');
    lines.push('      2      reverse +/− leads (undoes the flux negation directly)');
    lines.push('      3      normal');
    lines.push('      4      reverse +/− leads');
    lines.push('');
    lines.push('    Alternative: physically flip the board over instead of reversing leads —');
    lines.push('    the mirror\'s chirality reversal cancels the flux negation — then relabel');
    lines.push('    V↔W (the mirror swaps their physical positions). Flip and relabel are a');
    lines.push('    package: do both together, not one alone.');
    lines.push('');

    lines.push('  Clocking:');
    const boltCount = this.mountingHoles;
    const polePairs = this.params.numPoles / 2;
    const fmt = (n: number) => Number(n.toFixed(2)).toString();
    if (boltCount === 0) {
      lines.push('    No mounting-hole bolt circle is configured (mountingHoles = 0) — there are');
      lines.push('    no fixed rotational references to evaluate. Enable mounting holes for a');
      lines.push('    computed clocking recommendation for this pole/hole combination.');
    } else {
      const elecStepDeg = (360 / boltCount) * polePairs;
      const mod = Math.round((elecStepDeg % 360) * 1e6) / 1e6;
      if (mod === 0) {
        lines.push(`    Bolt circle: ${boltCount} holes × ${polePairs} pole pairs → each hole is`);
        lines.push(`    (360°/${boltCount}) × ${polePairs} = ${fmt(elecStepDeg)}° electrical, a whole multiple of`);
        lines.push('    360° — every mounting hole is electrically equivalent: free clocking. Pick');
        lines.push('    any hole for mechanical alignment; the +/− pad pairs double as a visual');
        lines.push('    alignment reference between stacked boards.');
      } else {
        lines.push(`    Bolt circle: ${boltCount} holes × ${polePairs} pole pairs → each hole is`);
        lines.push(`    (360°/${boltCount}) × ${polePairs} = ${fmt(elecStepDeg)}° electrical — a ${fmt(mod)}°`);
        lines.push('    electrical phase shift per hole (mod 360°); bolt positions are NOT electrically interchangeable.');
        lines.push(`    Rotating to a different hole shifts electrical phase by ${fmt(mod)}°.`);
      }
    }
    lines.push('    Phase-swap rewiring (wye/delta jumper choice, cyclic U→V→W relabeling) only');
    lines.push('    corrects misalignment in 120°-electrical steps — a continuous residual needs');
    lines.push('    mechanical registration (a keyed hole or an index mark), not rewiring alone.');

    return lines;
  }

  /**
   * README §"STACKING-TABS JOINT GUIDE" — extends the generic STACKING GUIDE
   * above with the physical assembly instructions for the edge-ear layout
   * (spec §2-§5, docs/superpowers/specs/2026-07-23-pcb-stator-stacking-tabs-design.md).
   * Rendered only when `terminalLayout === 'stacking-tabs'` (the caller already
   * guarantees `windingType === 'independent'`).
   *
   * Two branches:
   *  - `earLayoutAssessment.valid` is false (a + and − ear collide at this
   *    pole/coil combination): honest drop notice — same fact
   *    `pcb-stator-support.ts`'s banner already reports via
   *    `earLayoutAssessment.reasons` — mirroring the encoder module's
   *    `recessDropped` honesty pattern. No rotation table / joint instructions
   *    are printed, because none of that copper was actually generated.
   *  - valid: the full physical joint guide — per-stage rotation table (live
   *    D = `stackStageDeg`, `stackRotationRad`'s degree form), the mating-ear
   *    joint instruction, the series ×N decay-honesty note (verbatim per spec
   *    §2), the bolt-pitch/D alignment rule computed from THIS board's live
   *    mounting-hole bolt circle, the terminal callout, and the future
   *    rotor-separated-stages note.
   */
  private generateStackingTabsGuide(): string[] {
    const lines: string[] = [];
    lines.push('');
    lines.push('STACKING-TABS JOINT GUIDE (physical edge-ear stacking)');
    lines.push('─'.repeat(40));
    lines.push('');

    const assessment = this.earLayoutAssessment;
    if (!assessment.valid) {
      lines.push('  Stacking-tabs layout selected, but the ear layout was DROPPED at this');
      lines.push('  pole/coil combination — a + ear and a − ear collide, so no ear pads, tails,');
      lines.push('  or T3 drills were generated. The board fell back to the plain-circle outline');
      lines.push('  and the adjacent-pads return terminals described above (MOTOR TERMINALS /');
      lines.push('  BUS ROUTING already reflect what actually shipped, not the ears).');
      lines.push('');
      lines.push(`  ${assessment.reasons.join(' ')}`);
      lines.push('');
      lines.push('  Pick a pole/coil combination where polePairs does not divide numCoils (the');
      lines.push('  default 8-pole / 6-coil combination is clean), or switch back to the');
      lines.push('  adjacent terminal layout.');
      return lines;
    }

    const D = this.stackStageDeg;
    const fmt = (n: number) => Number(n.toFixed(2)).toString();

    lines.push('  Every stage is the SAME board, physically rotated about the shaft axis by');
    lines.push(`  one electrical period D = 360°/polePairs = ${fmt(D)}° at this board's live pole`);
    lines.push(`  count (${this.params.numPoles} poles → ${this.params.numPoles / 2} pole pairs) — every stage stays in phase with`);
    lines.push('  the shared rotor field by construction (D × polePairs ≡ 0 mod 360°).');
    lines.push('');
    lines.push('  Boards in a touching tab stack all face the same way and see the SAME gap —');
    lines.push('  they need NO parity correction. The STAGE PARITY section above (in the');
    lines.push('  generic STACKING GUIDE) assumes rotor-separated stages across alternating air');
    lines.push('  gaps, which does not describe this touching stack; it is a future extension,');
    lines.push('  not something to apply here.');
    lines.push('');
    lines.push('  Per-stage rotation table (stage n counts rotations of D from board 1, the');
    lines.push('  unrotated reference board):');
    lines.push('');
    lines.push('      Board   n    Rotation (n × D)');
    for (let n = 0; n < 4; n++) {
      lines.push(`      ${n + 1}       ${n}    ${fmt(n * D)}°`);
    }
    lines.push('');
    lines.push('  (2-4 boards between one rotor pair is the practical sweet spot — see Series');
    lines.push('  ×N below. Longer stacks repeat the same n × D pattern.)');
    lines.push('');

    lines.push('  Joint: align mating ears, wire snippet through each hole, solder both faces.');
    lines.push('    Rotating each successive board by +D lands board n\'s − ear at the SAME');
    lines.push('    azimuth as board (n+1)\'s + ear — stack the boards flush, face-to-face, in');
    lines.push('    that rotated sequence so each seam\'s two ears line up hole-for-hole. Thread');
    lines.push('    a short wire snippet through each aligned hole and solder both faces (or');
    lines.push('    solder-flood the aligned pair instead of using a snippet) — 3 holes per ear');
    lines.push('    means 3 snippets (or 3 flooded joints) per phase, per board-to-board seam.');
    lines.push('');

    lines.push('  Series ×N (ideal figure):');
    lines.push('    Stacking N boards this way puts each phase\'s windings in series, same as');
    lines.push('    the jumper-harness Series recipe above: volts × N, amps unchanged — IF every');
    lines.push('    board contributed the same EMF. It does not: the gap field decays axially on');
    lines.push('    roughly a pole-pitch/2π length scale (~10 mm at defaults), so each 1.6 mm');
    lines.push('    board sits deeper from the magnets and earns less EMF;');
    lines.push('    the practical sweet spot between one rotor pair is ~2–4 boards. × N is the');
    lines.push('    IDEAL figure — the voltage estimate above does NOT model this decay.');
    lines.push('');

    lines.push('  Bolt-circle alignment:');
    const boltCount = this.mountingHoles;
    if (boltCount === 0) {
      lines.push('    No mounting-hole bolt circle is configured (mountingHoles = 0) — there is no');
      lines.push('    fixture to check against D. Enable mounting holes for a computed alignment');
      lines.push('    check against this board\'s live pole count.');
    } else {
      const boltPitch = 360 / boltCount;
      const ratio = D / boltPitch;
      const nearestInt = Math.round(ratio);
      const divides = Math.abs(ratio - nearestInt) < 1e-6;
      lines.push('    Rule: the bolt-circle pitch (360°/mountingHoles) must divide D for a single');
      lines.push('    counter-fixture (a jig holding every stage square) to register all stages.');
      if (divides) {
        lines.push(`    This board: ${boltCount} holes → ${fmt(boltPitch)}° pitch divides D = ${fmt(D)}°`);
        lines.push(`    cleanly (${nearestInt}×) — a counter-fixture works at every stage.`);
      } else {
        lines.push(`    This board: ${boltCount} holes → ${fmt(boltPitch)}° pitch does NOT divide`);
        lines.push(`    D = ${fmt(D)}° — WARNING: a single fixed counter-fixture will NOT register`);
        lines.push('    every stage square. Register stages by the ear joints themselves (or an');
        lines.push('    index mark), not the bolt circle.');
      }
    }
    lines.push('');

    lines.push('  Terminals: drive from board 1\'s + ears; board N\'s − ears are the stack\'s far');
    lines.push('  end — both accessible at the ends of the stack, never buried mid-stack.');
    lines.push('');

    lines.push('  Future: rotor-separated stages (a documented non-goal here) would offset each');
    lines.push('  stage by a HALF electrical period (D/2) instead of D, with its own rotor pair');
    lines.push('  between adjacent boards, and pin bridges reusing these same ear holes to carry');
    lines.push('  the joint through the gap.');

    return lines;
  }
}
